import React from "react";
import {
  Heart, Battery, RefreshCw, Users, User, Shield,
  Sparkles, Zap, Compass, Anchor, Sprout, Sun, Moon, Flame,
  Star, Flag, Wind, Mountain, Feather, Target,
  Cloud, Droplet, Leaf, Coffee, Smile, MessageCircle,
  Hand, Bird, Waves,
} from "lucide-react";

const ACCENT = "#5e50f7";

// Fix-sequence icon pools for Offer V3 — keeps generated content predictable
// while still feeling varied across cards.
const PAIN_ICONS = [Battery, RefreshCw, Cloud];
const VICTORY_ICONS = [Heart, Sprout, Users];

// LLM-driven semantic icon picker for the roadmap. The LLM returns an icon
// name (e.g. "compass") from a controlled vocabulary; we map it here. Falls
// back to a pleasing default if the name is unknown.
const ROADMAP_ICON_MAP = {
  compass: Compass,
  sparkles: Sparkles,
  zap: Zap,
  sun: Sun,
  anchor: Anchor,
  flame: Flame,
  star: Star,
  flag: Flag,
  wind: Wind,
  mountain: Mountain,
  feather: Feather,
  target: Target,
  moon: Moon,
  droplet: Droplet,
  leaf: Leaf,
  smile: Smile,
  shield: Shield,
  heart: Heart,
  users: Users,
  sprout: Sprout,
  // Semantic aliases (LLM may emit these despite the controlled list).
  mirror: Compass,
  hand: Hand,
  bird: Bird,
  waves: Waves,
};
const ROADMAP_ICON_FALLBACK = [
  Compass, Sparkles, Zap, Sun, Anchor, Flame, Star, Flag,
  Wind, Mountain, Feather, Target, Moon, Droplet, Leaf, Smile,
];

/**
 * Field lookup with Hungarian-first, English fallback. Supports gradual
 * migration: new generations have Hungarian keys, older saved blueprints
 * keep their English keys until the user refines or regenerates.
 */
const F = (obj, ...keys) => {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return null;
};

/** Defensively flatten any value to a string we can render.
 *  Old cached blueprints sometimes store nested objects like
 *  { fo: "...", masodlagos: "..." } where a flat string is expected.
 *  This avoids "Objects are not valid as a React child" crashes.
 *  Also normalises long em-dashes to short hyphens.
 */
function toText(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v.replace(/\u2014/g, "-");
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.filter((x) => x !== null && x !== undefined).map(toText).join(", ");
  if (typeof v === "object") {
    return Object.values(v)
      .map(toText)
      .filter((s) => s && s.trim())
      .join(" - ");
  }
  return String(v);
}

/** Normalize vibe scales to a uniform [{ name, value }] array.
 *  Accepts:
 *    - { formal_vs_casual: "30", playful_vs_serious: "70" } (legacy object)
 *    - [ { nev: "formális vs laza", ertek: 30 }, ... ] (new Hungarian array)
 *    - [ { name, value }, ... ] (already normalized)
 */
function normalizeVibeScales(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((s) => ({
        name: s.nev || s.name || "",
        value: parseInt(s.ertek ?? s.value ?? 50, 10) || 50,
      }))
      .filter((s) => s.name);
  }
  return Object.entries(raw).map(([k, v]) => ({
    name: k.replace(/_/g, " "),
    value: parseInt(v, 10) || 50,
  }));
}

/* ----------------------- BRAND VOICE ----------------------- */

function KeyField({ eyebrow, value }) {
  const text = toText(value);
  if (!text) return null;
  return (
    <div
      style={{
        background: "white",
        border: "1px solid rgba(124,108,242,0.18)",
        borderRadius: 8,
        padding: "16px 20px",
        marginBottom: 24,
      }}
      data-testid="bp-key-field"
    >
      <Eyebrow>{eyebrow}</Eyebrow>
      <div style={{ fontSize: 18, color: "#0a0a12", fontWeight: 500, lineHeight: 1.3 }}>
        {text}
      </div>
    </div>
  );
}

/**
 * New 9-section Brand Voice Blueprint renderer. Activated when the blueprint
 * contains the new-schema markers (osszegzes or egy_mondatos_definicio).
 * Old saved blueprints fall through to the legacy renderer below.
 */
function renderVoiceBlueprintV2(bp) {
  const oneLine = F(bp, "egy_mondatos_definicio");
  const esszencia = F(bp, "esszencia") || {};
  const esszenciaCim = F(esszencia, "cim");
  const esszenciaSzoveg = F(esszencia, "szoveg");
  const archetype = F(bp, "archetipus") || {};
  const archFo = F(archetype, "fo") || {};
  const archMas = F(archetype, "masodlagos") || {};
  const kivk = F(bp, "ki_vagyok_ki_nem_vagyok") || {};
  const kiVagyok = F(kivk, "ki_vagyok");
  const kiNemVagyok = F(kivk, "ki_nem_vagyok");
  const tones = F(bp, "hangnem");
  const vibeScales = Array.isArray(F(bp, "vibe_skala")) ? F(bp, "vibe_skala") : [];
  const styleRaw = bp && Object.prototype.hasOwnProperty.call(bp, "stiluslenyomat")
    ? bp.stiluslenyomat
    : null;
  const hasStyleFingerprint =
    styleRaw &&
    typeof styleRaw === "object" &&
    ((Array.isArray(styleRaw.jellegzetes_szavak) && styleRaw.jellegzetes_szavak.length) ||
      (Array.isArray(styleRaw.mondatkezdesek) && styleRaw.mondatkezdesek.length) ||
      (typeof styleRaw.mondatritmus === "string" && styleRaw.mondatritmus.trim()) ||
      (typeof styleRaw.regiszter_megszolitas === "string" && styleRaw.regiszter_megszolitas.trim()) ||
      (styleRaw.cta_stilus && typeof styleRaw.cta_stilus === "object") ||
      (Array.isArray(styleRaw.elkotelezodes_technikak) && styleRaw.elkotelezodes_technikak.length) ||
      (typeof styleRaw.irasjelek_vizualis === "string" && styleRaw.irasjelek_vizualis.trim()) ||
      (typeof styleRaw.blokkszerkezet === "string" && styleRaw.blokkszerkezet.trim()));
  const avoid = F(bp, "kerulendo_szavak");
  const osszegzes = F(bp, "osszegzes");

  return (
    <div className="bp-stack bp-body" data-testid="brand-voice-report-v2">
      {/* SECTION 1 - EGY MONDATBAN */}
      {oneLine && (
        <div
          style={{
            background: "rgba(124,108,242,0.06)",
            border: "1px solid rgba(124,108,242,0.22)",
            borderRadius: 10,
            padding: "18px 22px",
            marginBottom: 28,
          }}
          data-testid="voice-section-definition"
        >
          <div
            style={{
              fontSize: 14,
              color: "#7c6cf2",
              letterSpacing: "0.12em",
              fontWeight: 600,
              marginBottom: 14,
              textTransform: "uppercase",
            }}
          >
            EGY MONDATBAN
          </div>
          <div
            style={{
              fontSize: 17,
              color: "#0a0a12",
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            {toText(oneLine)}
          </div>
        </div>
      )}

      {/* SECTION 2 - ESSZENCIA */}
      {(esszenciaCim || esszenciaSzoveg) && (
        <div style={{ marginBottom: 28 }} data-testid="voice-section-esszencia">
          <VEyebrow>A TE HANGOD</VEyebrow>
          {esszenciaCim && (
            <div
              style={{
                fontSize: 19,
                fontWeight: 600,
                color: "#0a0a12",
                letterSpacing: "-0.01em",
                lineHeight: 1.3,
                marginBottom: 12,
              }}
            >
              {toText(esszenciaCim)}
            </div>
          )}
          {esszenciaSzoveg && (
            <div style={{ fontSize: 14, color: "#1a1a28", lineHeight: 1.7 }}>
              {toText(esszenciaSzoveg)}
            </div>
          )}
        </div>
      )}

      {/* SECTION 3 - ARCHETÍPUS (two cards) */}
      {(F(archFo, "nev") || F(archMas, "nev")) && (
        <div style={{ marginBottom: 28 }} data-testid="voice-section-archetype">
          <VEyebrow>ARCHETÍPUS</VEyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <ArchCard
              subEyebrow="FŐ ARCHETÍPUS"
              nev={F(archFo, "nev")}
              leiras={F(archFo, "leiras")}
              borderOpacity={0.20}
            />
            <ArchCard
              subEyebrow="MÁSODLAGOS"
              nev={F(archMas, "nev")}
              leiras={F(archMas, "leiras")}
              borderOpacity={0.14}
            />
          </div>
        </div>
      )}

      {/* SECTION 4 - KI VAGYOK / KI NEM VAGYOK */}
      {(Array.isArray(kiVagyok) && kiVagyok.length > 0) ||
      (Array.isArray(kiNemVagyok) && kiNemVagyok.length > 0) ? (
        <div style={{ marginBottom: 28 }} data-testid="voice-section-identity">
          <VEyebrow>KI VAGYOK / KI NEM VAGYOK</VEyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Ki vagyok - green */}
            <div
              style={{
                background: "rgba(76,181,133,0.08)",
                border: "1px solid rgba(76,181,133,0.22)",
                borderRadius: 8,
                padding: "14px 16px",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "#2a8a5a",
                  letterSpacing: "0.14em",
                  fontWeight: 600,
                  marginBottom: 8,
                  textTransform: "uppercase",
                }}
              >
                KI VAGYOK
              </div>
              {(kiVagyok || []).map((it, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 13,
                    color: "#1a1a28",
                    lineHeight: 1.65,
                    marginBottom: 6,
                  }}
                >
                  <span style={{ color: "#2a8a5a", marginRight: 6 }}>·</span>
                  {toText(it)}
                </div>
              ))}
            </div>
            {/* Ki nem vagyok - red */}
            <div
              style={{
                background: "rgba(214,128,128,0.06)",
                border: "1px solid rgba(214,128,128,0.20)",
                borderRadius: 8,
                padding: "14px 16px",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "#b85a5a",
                  letterSpacing: "0.14em",
                  fontWeight: 600,
                  marginBottom: 8,
                  textTransform: "uppercase",
                }}
              >
                KI NEM VAGYOK
              </div>
              {(kiNemVagyok || []).map((it, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 13,
                    color: "#1a1a28",
                    lineHeight: 1.65,
                    marginBottom: 6,
                    textDecoration: "line-through",
                    textDecorationColor: "rgba(180,80,80,0.4)",
                  }}
                >
                  {toText(it)}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* SECTION 5 - HANGNEM (pill cluster) */}
      {Array.isArray(tones) && tones.length > 0 && (
        <div style={{ marginBottom: 28 }} data-testid="voice-section-tones">
          <VEyebrow>HANGNEM</VEyebrow>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {tones.map((t, i) => (
              <span
                key={i}
                style={{
                  background: "rgba(124,108,242,0.10)",
                  border: "1px solid rgba(124,108,242,0.22)",
                  color: "#5e4cd8",
                  padding: "5px 12px",
                  borderRadius: 14,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {toText(t)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* SECTION 6 - VIBE SKÁLA (graphical bars) */}
      {vibeScales.length > 0 && (
        <div style={{ marginBottom: 28 }} data-testid="voice-section-vibe">
          <VEyebrow>VIBE SKÁLA</VEyebrow>
          {vibeScales.map((s, i) => {
            const ertek = parseInt(s.ertek ?? s.value ?? 50, 10) || 0;
            const nev = s.nev || s.name || "";
            const magyarazat = s.magyarazat || "";
            return (
              <div key={i}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 13, color: "#1a1a28" }}>{nev}</span>
                  <span style={{ fontSize: 11, color: "#5e4cd8", fontWeight: 600 }}>
                    {ertek}
                  </span>
                </div>
                <div
                  style={{
                    height: 5,
                    background: "rgba(124,108,242,0.14)",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.max(0, Math.min(100, ertek))}%`,
                      background: "linear-gradient(90deg, #7c6cf2, #a89bff)",
                    }}
                  />
                </div>
                {magyarazat && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#4a4a5c",
                      lineHeight: 1.55,
                      marginTop: 4,
                      marginBottom: 14,
                    }}
                  >
                    {toText(magyarazat)}
                  </div>
                )}
                {!magyarazat && <div style={{ marginBottom: 14 }} />}
              </div>
            );
          })}
        </div>
      )}

      {/* SECTION 7 - STÍLUSLENYOMAT (conditional, 8 sub-blocks) */}
      {hasStyleFingerprint && (
        <div style={{ marginBottom: 28 }} data-testid="voice-section-fingerprint">
          <div style={{ marginBottom: 14 }}>
            <span
              style={{
                fontSize: 14,
                color: "#7c6cf2",
                letterSpacing: "0.12em",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              STÍLUSLENYOMAT
            </span>
            <span
              style={{
                fontSize: 11,
                color: "#6a6a80",
                fontWeight: 400,
                marginLeft: 8,
                letterSpacing: "0.04em",
                textTransform: "none",
              }}
            >
              - az írásmintád alapján
            </span>
          </div>

          {/* A) Jellegzetes szóhasználat */}
          {Array.isArray(styleRaw.jellegzetes_szavak) && styleRaw.jellegzetes_szavak.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <FpHead>Jellegzetes szóhasználat</FpHead>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {styleRaw.jellegzetes_szavak.map((w, i) => (
                  <span
                    key={i}
                    style={{
                      background: "rgba(124,108,242,0.06)",
                      border: "1px solid rgba(124,108,242,0.16)",
                      color: "#1a1a28",
                      padding: "4px 10px",
                      borderRadius: 12,
                      fontSize: 11,
                    }}
                  >
                    {toText(w)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* B) Saját mondatkezdéseid */}
          {Array.isArray(styleRaw.mondatkezdesek) && styleRaw.mondatkezdesek.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <FpHead>Saját mondatkezdéseid</FpHead>
              <div>
                {styleRaw.mondatkezdesek.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 13,
                      color: "#1a1a28",
                      lineHeight: 1.7,
                      fontStyle: "italic",
                    }}
                  >
                    <span style={{ marginRight: 6, fontStyle: "normal" }}>·</span>
                    {toText(m)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* C) Mondatritmus */}
          {typeof styleRaw.mondatritmus === "string" && styleRaw.mondatritmus.trim() && (
            <div style={{ marginBottom: 18 }}>
              <FpHead>Mondatritmus</FpHead>
              <div style={{ fontSize: 13, color: "#1a1a28", lineHeight: 1.65 }}>
                {toText(styleRaw.mondatritmus)}
              </div>
            </div>
          )}

          {/* D) Regiszter és megszólítás */}
          {typeof styleRaw.regiszter_megszolitas === "string" &&
            styleRaw.regiszter_megszolitas.trim() && (
              <div style={{ marginBottom: 18 }}>
                <FpHead>Regiszter és megszólítás</FpHead>
                <div style={{ fontSize: 13, color: "#1a1a28", lineHeight: 1.65 }}>
                  {toText(styleRaw.regiszter_megszolitas)}
                </div>
              </div>
            )}

          {/* E) CTA-stílus (whole sub-block conditional on cta_stilus presence) */}
          {styleRaw.cta_stilus && typeof styleRaw.cta_stilus === "object" && (
            <div style={{ marginBottom: 18 }}>
              <FpHead>CTA-stílus</FpHead>
              {Array.isArray(styleRaw.cta_stilus.minta_cta_k) &&
                styleRaw.cta_stilus.minta_cta_k.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {styleRaw.cta_stilus.minta_cta_k.map((c, i) => (
                      <span
                        key={i}
                        style={{
                          background: "rgba(124,108,242,0.10)",
                          border: "1px solid rgba(124,108,242,0.22)",
                          color: "#5e4cd8",
                          padding: "4px 11px",
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 500,
                        }}
                      >
                        {toText(c)}
                      </span>
                    ))}
                  </div>
                )}
              {typeof styleRaw.cta_stilus.elv === "string" && styleRaw.cta_stilus.elv.trim() && (
                <div style={{ fontSize: 13, color: "#1a1a28", lineHeight: 1.65 }}>
                  {toText(styleRaw.cta_stilus.elv)}
                </div>
              )}
            </div>
          )}

          {/* F) Elköteleződés-építő technikák */}
          {Array.isArray(styleRaw.elkotelezodes_technikak) &&
            styleRaw.elkotelezodes_technikak.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <FpHead>Elköteleződés-építő technikák</FpHead>
                <div>
                  {styleRaw.elkotelezodes_technikak.map((t, i) => (
                    <div
                      key={i}
                      style={{ fontSize: 13, color: "#1a1a28", lineHeight: 1.7 }}
                    >
                      <span style={{ marginRight: 6 }}>·</span>
                      {toText(t)}
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* G) Írásjelek és vizuális jelölők */}
          {typeof styleRaw.irasjelek_vizualis === "string" &&
            styleRaw.irasjelek_vizualis.trim() && (
              <div style={{ marginBottom: 18 }}>
                <FpHead>Írásjelek és vizuális jelölők</FpHead>
                <div style={{ fontSize: 13, color: "#1a1a28", lineHeight: 1.65 }}>
                  {toText(styleRaw.irasjelek_vizualis)}
                </div>
              </div>
            )}

          {/* H) Bekezdés- és blokkszerkezet */}
          {typeof styleRaw.blokkszerkezet === "string" && styleRaw.blokkszerkezet.trim() && (
            <div>
              <FpHead>Bekezdés- és blokkszerkezet</FpHead>
              <div style={{ fontSize: 13, color: "#1a1a28", lineHeight: 1.65 }}>
                {toText(styleRaw.blokkszerkezet)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SECTION 8 - KERÜLENDŐ SZAVAK ÉS HANGNEMEK */}
      {Array.isArray(avoid) && avoid.length > 0 && (
        <div style={{ marginBottom: 28 }} data-testid="voice-section-avoid">
          <VEyebrow>KERÜLENDŐ SZAVAK ÉS HANGNEMEK</VEyebrow>
          <div
            style={{
              background: "rgba(214,128,128,0.05)",
              border: "1px solid rgba(214,128,128,0.16)",
              borderRadius: 8,
              padding: "14px 16px",
            }}
          >
            {avoid.map((it, i) => (
              <div
                key={i}
                style={{
                  fontSize: 13,
                  color: "#1a1a28",
                  lineHeight: 1.8,
                  textDecoration: "line-through",
                  textDecorationColor: "rgba(180,80,80,0.5)",
                }}
              >
                {toText(it)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECTION 9 - ÖSSZEGZÉS */}
      {osszegzes && (
        <div data-testid="voice-section-summary">
          <VEyebrow>A TE HANGOD, EGYBEN - ÖSSZEGZÉS</VEyebrow>
          <div
            style={{
              fontSize: 14,
              color: "#1a1a28",
              lineHeight: 1.75,
              fontStyle: "italic",
            }}
          >
            {toText(osszegzes)
              .split(/\n\n+/)
              .map((p) => p.replace(/^\s*\*+\s*|\s*\*+\s*$/g, "").trim())
              .filter((p) => p)
              .map((p, i) => (
                <p key={i} style={{ marginBottom: "1em" }}>
                  {p}
                </p>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FpHead({ children }) {
  return (
    <div
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: "#7c6cf2",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

/** Section eyebrow used in the Brand Voice v2 report. Larger and tighter
 *  letter-spacing than the legacy `Eyebrow` (which Avatar / Offer keep using). */
function VEyebrow({ children }) {
  return (
    <div
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: "#7c6cf2",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

function ArchCard({ subEyebrow, nev, leiras, borderOpacity }) {
  if (!nev && !leiras) return null;
  return (
    <div
      style={{
        background: "white",
        border: `1px solid rgba(124,108,242,${borderOpacity})`,
        borderRadius: 8,
        padding: "14px 16px",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "#5e4cd8",
          letterSpacing: "0.14em",
          fontWeight: 600,
          marginBottom: 6,
          textTransform: "uppercase",
        }}
      >
        {subEyebrow}
      </div>
      {nev && (
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#0a0a12",
            marginBottom: 6,
          }}
        >
          {toText(nev)}
        </div>
      )}
      {leiras && (
        <div style={{ fontSize: 13, color: "#1a1a28", lineHeight: 1.55 }}>
          {toText(leiras)}
        </div>
      )}
    </div>
  );
}

export function renderVoiceBlueprint(bp) {
  if (!bp) return null;
  // Detect new 9-section schema. The new generator always emits either
  // `osszegzes` or `egy_mondatos_definicio`; legacy blueprints have neither.
  const isNewSchema =
    Object.prototype.hasOwnProperty.call(bp, "osszegzes") ||
    Object.prototype.hasOwnProperty.call(bp, "egy_mondatos_definicio") ||
    Object.prototype.hasOwnProperty.call(bp, "ki_vagyok_ki_nem_vagyok");
  if (isNewSchema) return renderVoiceBlueprintV2(bp);

  // Legacy renderer (old saved blueprints only)
  const archetype = F(bp, "archetipus", "archetype");
  const tagline = F(bp, "szlogen", "tagline");
  const whoIAm = F(bp, "ki_vagyok", "who_i_am");
  const whoIAmNot = F(bp, "ki_nem_vagyok", "who_i_am_not");
  const tones = F(bp, "hangnem", "tone_descriptors");
  const phrases = F(bp, "jellegzetes_kifejezesek", "signature_phrases");
  const avoid = F(bp, "kerulendo_szavak", "words_to_avoid");
  const sample = F(bp, "mintaszoveg", "sample_paragraph");
  const vibeRaw = F(bp, "vibe_skala", "vibe_scales");
  const vibeScales = normalizeVibeScales(vibeRaw);

  return (
    <div className="bp-stack bp-body">
      {tagline && <KeyField eyebrow="SZLOGEN" value={`"${toText(tagline)}"`} />}
      <Block label="ARCHETÍPUS" body={archetype} />
      <Block label="KI VAGYOK" body={whoIAm} />
      <Block label="KI NEM VAGYOK" body={whoIAmNot} />
      {Array.isArray(tones) && tones.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <Eyebrow>HANGNEM</Eyebrow>
          <div className="flex flex-wrap gap-1.5">
            {tones.map((t, i) => (
              <span
                key={i}
                className="px-2.5 py-1 rounded-full text-[11px]"
                style={{
                  background: "rgba(124,108,242,0.08)",
                  color: "#3a2a4a",
                  border: "1px solid rgba(124,108,242,0.18)",
                }}
              >
                {toText(t)}
              </span>
            ))}
          </div>
        </div>
      )}
      {vibeScales.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <Eyebrow>VIBE SKÁLA</Eyebrow>
          <div className="space-y-3">
            {vibeScales.map((s, i) => (
              <div key={i}>
                <div
                  className="flex justify-between text-[11px] mb-1"
                  style={{ color: "#6a6a78" }}
                >
                  <span>{s.name}</span>
                  <span>{s.value}</span>
                </div>
                <div
                  className="h-1.5 rounded-full"
                  style={{ background: "rgba(10,10,18,0.06)" }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${s.value}%`, background: ACCENT }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(phrases) && phrases.length > 0 && (
        <List label="JELLEGZETES KIFEJEZÉSEK" items={phrases} />
      )}
      {Array.isArray(avoid) && avoid.length > 0 && (
        <List label="KERÜLENDŐ SZAVAK" items={avoid} muted />
      )}
      {sample && (
        <Block label="MINTASZÖVEG" body={<span className="italic">{toText(sample)}</span>} />
      )}
    </div>
  );
}


/* ====================================================================
   AVATAR V2 renderer — Ideális Ügyfélprofil (20 szekciós).
   Forward-only: csak akkor hívódik, ha bp.version === "v2".
   F3-stílus: krémszínű háttér, lila eyebrow-k, kártyák, pillék.

   Kritikus rész (LÉPÉS 1) = 7 szekció a tartalom élén:
     Forrás · Esszencia · Alapok · Valóság · Fájdalom · Vágy · Belső.
   Kiegészítő rész (LÉPÉS 2) = 13 lenyitható szekció a végén
     (alapból összehúzva).
   ==================================================================== */

const V2_PURPLE = "#7c6cf2";
const V2_PURPLE_DEEP = "#5e4cd8";
const V2_PURPLE_BG_SOFT = "rgba(94,80,247,0.06)";
const V2_PURPLE_BORDER_SOFT = "rgba(94,80,247,0.18)";
const V2_INK = "#15141c";
const V2_INK_SOFT = "#2a2a3a";
const V2_MUTED = "#6a6a7a";

// Per-section accent palette. Used for eyebrow color, sub-field labels,
// numbered badges and KÖZÖS MINTA stripe.
const V2_ACCENT = {
  purple: {
    eyebrow: V2_PURPLE,
    label: V2_PURPLE_DEEP,
    badgeBg: "rgba(94,80,247,0.10)",
    badgeColor: V2_PURPLE_DEEP,
    stripeBg: V2_PURPLE_BG_SOFT,
    stripeBorder: V2_PURPLE,
    cardBorder: V2_PURPLE_BORDER_SOFT,
  },
  red: {
    eyebrow: "#c04848",
    label: "#9b3838",
    badgeBg: "rgba(192,72,72,0.10)",
    badgeColor: "#9b3838",
    stripeBg: "rgba(192,72,72,0.06)",
    stripeBorder: "#c04848",
    cardBorder: "rgba(192,72,72,0.20)",
  },
  green: {
    eyebrow: "#3a9d6e",
    label: "#2a8a5a",
    badgeBg: "rgba(58,157,110,0.10)",
    badgeColor: "#2a8a5a",
    stripeBg: "rgba(58,157,110,0.06)",
    stripeBorder: "#3a9d6e",
    cardBorder: "rgba(58,157,110,0.22)",
  },
};

function V2SectionHeader({ num, label, title, accent = "purple" }) {
  const a = V2_ACCENT[accent] || V2_ACCENT.purple;
  const eyebrowText = num && label ? `${num} · ${label}` : (num || label || "");
  return (
    <div style={{ marginBottom: 24 }} data-print-title>
      {eyebrowText && (
        <div style={{
          fontSize: 14, fontWeight: 500, color: a.eyebrow,
          letterSpacing: "0.10em", textTransform: "uppercase",
          marginBottom: 12,
        }}>{eyebrowText}</div>
      )}
      {title && (
        <div style={{
          fontSize: 23, fontWeight: 600, color: V2_INK,
          letterSpacing: "-0.012em", lineHeight: 1.2,
        }}>{title}</div>
      )}
    </div>
  );
}

function V2Pill({ children }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "5px 14px", borderRadius: 999,
      background: V2_PURPLE_BG_SOFT,
      border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
      color: V2_PURPLE_DEEP,
      fontSize: 13, fontWeight: 600,
      letterSpacing: "0.04em", textTransform: "uppercase",
      whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function V2PillRow({ children }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 }}>
      {children}
    </div>
  );
}

function V2KeyValueGrid({ entries }) {
  const items = entries.filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (items.length === 0) return null;
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      gap: "12px 24px",
      marginBottom: 18,
    }}>
      {items.map(([k, v]) => (
        <div key={k} style={{ fontSize: 15, lineHeight: 1.65, color: V2_INK_SOFT }}>
          <span style={{
            fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
            letterSpacing: "0.08em", textTransform: "uppercase",
            marginRight: 8, display: "block", marginBottom: 2,
          }}>{k}</span>
          {toText(v)}
        </div>
      ))}
    </div>
  );
}

function V2List({ items, label, accent = "purple" }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const a = V2_ACCENT[accent] || V2_ACCENT.purple;
  return (
    <div style={{ marginBottom: 18 }}>
      {label && (
        <div style={{
          fontSize: 13, fontWeight: 600, color: a.label,
          letterSpacing: "0.10em", textTransform: "uppercase",
          marginBottom: 10,
        }}>{label}</div>
      )}
      <ul style={{ margin: 0, paddingLeft: 18, listStyle: "none" }}>
        {items.map((it, i) => (
          <li key={i} style={{
            position: "relative", paddingLeft: 16,
            marginBottom: 8, lineHeight: 1.65, color: V2_INK_SOFT,
            fontSize: 15,
          }}>
            <span style={{
              position: "absolute", left: 0, top: 10,
              width: 6, height: 6, borderRadius: 999,
              background: a.eyebrow,
            }} />
            {toText(it)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function V2Table({ rows, columns, testid }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return (
    <div style={{ overflowX: "auto", marginTop: 12, marginBottom: 12 }} data-testid={testid}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{
                textAlign: "left", padding: "12px 14px",
                background: V2_PURPLE_BG_SOFT,
                color: V2_PURPLE_DEEP, fontWeight: 600,
                borderBottom: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
                fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase",
              }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key} style={{
                  padding: "12px 14px",
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                  verticalAlign: "top",
                  color: V2_INK_SOFT, lineHeight: 1.6, fontSize: 15,
                }}>{toText(r?.[c.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function V2Collapsible({ num, label, title, children, defaultOpen = false, titleStyle }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const eyebrowText = num && label ? `${num} · ${label}` : (num || label || "");
  return (
    <div style={{
      marginBottom: 14,
      border: "1px solid rgba(94,80,247,0.18)",
      borderRadius: 14,
      background: "rgba(255,255,255,0.6)",
      overflow: "hidden",
    }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: "100%", textAlign: "left",
          padding: "22px 28px",
          background: "transparent",
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          gap: 18,
          fontFamily: "inherit",
        }}
        data-testid={`v2-collapsible-${(title || eyebrowText || "").toLowerCase().replace(/\s+/g, "-")}`}
      >
        <div style={{ flex: 1 }}>
          {eyebrowText && (
            <div style={{
              fontSize: 14, fontWeight: 500, color: V2_PURPLE,
              letterSpacing: "0.10em", textTransform: "uppercase",
              marginBottom: 12,
            }}>{eyebrowText}</div>
          )}
          {title && (
            <div style={{
              fontSize: 22, fontWeight: 600, color: V2_INK,
              lineHeight: 1.35, letterSpacing: "-0.01em",
              ...(titleStyle || {}),
            }}>{title}</div>
          )}
        </div>
        <span style={{
          color: V2_PURPLE, fontSize: 18, fontWeight: 400,
          width: 28, height: 28, borderRadius: 999,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: V2_PURPLE_BG_SOFT, flexShrink: 0,
          transition: "transform 200ms ease",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
        }}>▾</span>
      </button>
      {open && (
        <div style={{
          padding: "0 28px 28px",
          fontSize: 15, color: V2_INK_SOFT, lineHeight: 1.75,
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

function renderAvatarBlueprintV2(bp) {
  if (!bp) return null;

  const fm = F(bp, "forrasminoseg") || {};
  const identitas = F(bp, "identitas") || {};
  const trendek = F(bp, "trendek") || {};
  const profil = F(bp, "avatar_profil") || {};
  const profilNev = F(profil, "profilnev");
  const profilLeiras = F(profil, "rovid_leiras");
  const rovid = F(bp, "avatar_rovid_osszefoglalo");
  const alap = F(bp, "alapadatok_es_kontextus") || {};
  const demo = F(alap, "demografia") || {};
  const ktx = F(alap, "kontextus") || {};
  const ehv = F(bp, "elethelyzet_es_valosag") || {};
  const t5p = F(bp, "top_5_problema") || {};
  const t5v = F(bp, "top_5_vagy") || {};
  const naplo = F(bp, "naplobejegyzes");

  // Supplementary (LÉPÉS 2) — 13 szekció a háttérben
  const bm = F(bp, "belso_monolog");
  const usz = F(bp, "ugyfelnyelv_szotar");
  const flk = F(bp, "felelmek_kifogasok");
  const dp = F(bp, "dontesi_pszichologia");
  const mvi = F(bp, "mire_van_igazan_szuksege");
  const ke = F(bp, "kommunikacios_erzekenysegek");
  const mpm = F(bp, "mit_probalt_mar");
  const tkp = F(bp, "tartalmi_kivalto_pontok");
  const ki = F(bp, "kontextualis_inspiraciok");
  const med = F(bp, "mediafogyasztas");
  const rftab = F(bp, "reszletes_forditotablazat");
  const kta = F(bp, "kiegeszito_tartalmi_alapanyagok");
  const ta = F(bp, "tartalmi_alapösszefoglalo") || F(bp, "tartalmi_alaposszefoglalo");
  const hasSupp = !!(bm || usz || flk || dp || mvi || ke || mpm || tkp || ki || med || rftab || kta || ta);

  const suppStatus = bp.supplementary_status || (hasSupp ? "done" : "loading");
  const suppError = bp._supp_error;
  const onSuppRetry = bp._supp_retry;
  const onRetryFailedSections = bp._retry_failed_sections;

  // Detect supplementary sections that have `_error` flag (e.g. budget ran out
  // during parallel calls). When any are present, we show a banner with a
  // button that calls /foundation/avatar/sections/retry-failed and ONLY
  // regenerates the failed ones (plus the 19 synthesis, which depends on all).
  const SUPP_SECTION_KEYS = [
    "belso_monolog", "ugyfelnyelv_szotar", "felelmek_kifogasok",
    "dontesi_pszichologia", "mire_van_igazan_szuksege",
    "kommunikacios_erzekenysegek", "mit_probalt_mar",
    "tartalmi_kivalto_pontok", "kontextualis_inspiraciok",
    "mediafogyasztas", "reszletes_forditotablazat",
    "kiegeszito_tartalmi_alapanyagok", "forrasminoseg",
    "tartalmi_alapösszefoglalo", "tartalmi_alaposszefoglalo",
    "identitas", "trendek",
  ];
  // Friendly Hungarian labels per section_id (matches the actual headers below).
  const _SUPP_LABELS = {
    belso_monolog: "07 · Belső monológ",
    ugyfelnyelv_szotar: "08 · Ügyfélnyelv szótár",
    felelmek_kifogasok: "09 · Félelmek és kifogások",
    dontesi_pszichologia: "10 · Döntési pszichológia",
    mire_van_igazan_szuksege: "11 · Mire van igazán szüksége",
    kommunikacios_erzekenysegek: "12 · Kommunikációs érzékenységek",
    mit_probalt_mar: "13 · Mit próbált már",
    tartalmi_kivalto_pontok: "14 · Tartalmi kiváltó pontok",
    kontextualis_inspiraciok: "15 · Kontextuális inspirációk",
    mediafogyasztas: "16 · Médiafogyasztás",
    reszletes_forditotablazat: "17 · Részletes fordítótáblázat",
    kiegeszito_tartalmi_alapanyagok: "18 · Kiegészítő tartalmi alapanyagok",
    forrasminoseg: "22 · Forrás minősége",
    "tartalmi_alapösszefoglalo": "21 · Tartalmi alapösszefoglaló",
    "tartalmi_alaposszefoglalo": "21 · Tartalmi alapösszefoglaló",
    identitas: "19 · Identitás",
    trendek: "20 · Trendek / Piaci radar",
  };
  const failedSuppSections = SUPP_SECTION_KEYS.filter((k) => {
    const v = bp[k];
    return v && typeof v === "object" && !Array.isArray(v) && "_error" in v;
  });
  const hasFailedSuppSections = failedSuppSections.length > 0;

  return (
    <div style={{
      background: "#f7f3eb", padding: "48px 52px",
      borderRadius: 14, color: V2_INK,
    }} data-testid="avatar-v2-blueprint">

      {/* === Hibás szekciók banner - csak akkor, ha bármely supp szekcióban `_error` van === */}
      {hasFailedSuppSections && suppStatus !== "loading" && onRetryFailedSections && (
        <div
          data-testid="avatar-v2-failed-sections-banner"
          style={{
            background: "rgba(255, 220, 200, 0.55)",
            border: "1px solid rgba(214, 120, 65, 0.35)",
            borderRadius: 12, padding: "18px 22px", marginBottom: 32,
            display: "flex", alignItems: "flex-start", justifyContent: "space-between",
            gap: 18, flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#a64418",
              letterSpacing: "0.14em", textTransform: "uppercase",
              marginBottom: 8,
            }}>
              {failedSuppSections.length} szekció nem generálódott
            </div>
            <div style={{ fontSize: 14, color: V2_INK, lineHeight: 1.55, marginBottom: 6 }}>
              Az alábbi szekciók hibára futottak (gyakran azért, mert az LLM kredit kifogyott közben):
            </div>
            <div style={{ fontSize: 13, color: V2_INK_SOFT, lineHeight: 1.7 }}>
              {failedSuppSections.map((k) => _SUPP_LABELS[k] || k).join(" · ")}
            </div>
          </div>
          <button
            type="button"
            data-testid="avatar-v2-retry-failed-sections-btn"
            onClick={onRetryFailedSections}
            style={{
              background: "#3a2e22", color: "#fff5e8",
              border: "none", borderRadius: 999,
              padding: "11px 22px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
              letterSpacing: "0.01em", whiteSpace: "nowrap",
              flexShrink: 0, alignSelf: "center",
              transition: "background 0.16s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#2a2118"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#3a2e22"; }}
          >
            Hibás szekciók újragenerálása
          </button>
        </div>
      )}

      {/* === Avatar profil (rövid, emberi profilnév kártya — a hosszú összefoglaló előtt) === */}
      {(profilNev || profilLeiras) && (
        <section style={{ marginBottom: 40 }} data-testid="v2-sec-avatar-profil">
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 22,
            background: "rgba(255,255,255,0.78)",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 16,
            padding: "26px 30px",
            boxShadow: "0 1px 12px rgba(0,0,0,0.04)",
          }}>
            <div style={{
              width: 58, height: 58, borderRadius: 999, flexShrink: 0,
              background: "rgba(94,80,247,0.12)", color: V2_PURPLE_DEEP,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }} aria-hidden="true">
              <Compass size={26} strokeWidth={1.6} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: V2_PURPLE,
                letterSpacing: "0.14em", textTransform: "uppercase",
                marginBottom: 8,
              }}>Avatar profil</div>
              {profilNev && (
                <div style={{
                  fontSize: 24, fontWeight: 700, color: V2_INK,
                  lineHeight: 1.2, letterSpacing: "-0.01em",
                  marginBottom: profilLeiras ? 10 : 0,
                }}>{toText(profilNev)}</div>
              )}
              {profilLeiras && (
                <div style={{
                  fontSize: 15, color: V2_INK_SOFT, lineHeight: 1.65,
                }}>{toText(profilLeiras)}</div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* === 01 · ESSZENCIA === */}
      {rovid && (
        <section style={{ marginBottom: 56 }} data-testid="v2-sec-esszencia">
          <V2SectionHeader num="01" label="ESSZENCIA" title="Avatar rövid összefoglaló" />
          <div style={{
            background: "linear-gradient(135deg, rgba(94,80,247,0.10), rgba(94,80,247,0.04))",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14,
            padding: "32px 36px",
            fontSize: 15,
            lineHeight: 1.75,
            color: V2_INK,
            boxShadow: "0 0 24px rgba(94,80,247,0.06)",
          }}>
            {toText(rovid)}
          </div>
        </section>
      )}

      {/* === 02 · ALAPOK === */}
      <section style={{ marginBottom: 56 }} data-testid="v2-sec-alapok">
        <V2SectionHeader num="02" label="ALAPOK" title="Alapadatok és kontextus" />
        <div style={{ marginBottom: 24 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 12,
          }}>Demográfia</div>
          <V2KeyValueGrid entries={[
            ["Életkor", demo.eletkor],
            ["Nem", demo.nem],
            ["Családi helyzet", demo.csaladi_helyzet],
            ["Lakóhely / piac", demo.lakohely_piac],
            ["Foglalkozás", demo.foglalkozas],
            ["Jövedelmi kontextus", demo.jovedelmi_kontextus],
            ["Képzettség", demo.kepzettseg],
            ["Élethelyzeti szakasz", demo.elethelyzeti_szakasz],
          ]} />
        </div>
        <div>
          <div style={{
            fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 12,
          }}>Kontextus</div>
          {ktx.hol_tart_most && (
            <p style={{ margin: "0 0 14px", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
              <strong style={{ color: V2_INK }}>Hol tart most: </strong>{toText(ktx.hol_tart_most)}
            </p>
          )}
          {ktx.dontesi_pont && (
            <p style={{ margin: "0 0 14px", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
              <strong style={{ color: V2_INK }}>Döntési pont: </strong>{toText(ktx.dontesi_pont)}
            </p>
          )}
          {ktx.kulso_nyomas && (
            <p style={{ margin: "0 0 14px", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
              <strong style={{ color: V2_INK }}>Külső nyomás: </strong>{toText(ktx.kulso_nyomas)}
            </p>
          )}
          {ktx.belso_nyomas && (
            <p style={{ margin: "0 0 14px", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
              <strong style={{ color: V2_INK }}>Belső nyomás: </strong>{toText(ktx.belso_nyomas)}
            </p>
          )}
          {ktx.szerepek_orloese && (
            <p style={{ margin: "0 0 18px", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
              <strong style={{ color: V2_INK }}>Szerepek őrlése: </strong>{toText(ktx.szerepek_orloese)}
            </p>
          )}
          <V2List label="Napi kihívások" items={ktx.napi_kihivasok} />
        </div>
      </section>

      {/* === 03 · VALÓSÁG === */}
      <section style={{ marginBottom: 56 }} data-testid="v2-sec-valosag">
        <V2SectionHeader num="03" label="VALÓSÁG" title="Élethelyzet és mindennapi valóság" />
        <div style={{ marginBottom: 24 }}>
          {Array.isArray(ehv.bekezdesek) && ehv.bekezdesek.map((p, i) => (
            <p key={i} style={{
              margin: "0 0 14px", lineHeight: 1.65,
              color: V2_INK_SOFT, fontSize: 15,
            }}>{toText(p)}</p>
          ))}
        </div>
        {Array.isArray(ehv.konkret_helyzetek) && ehv.konkret_helyzetek.length > 0 && (
          <div style={{
            background: "rgba(255,255,255,0.55)",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14, padding: "28px 32px",
          }}>
            <div style={{
              fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
              letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14,
            }}>Konkrét visszatérő helyzetek</div>
            <ul style={{ margin: 0, paddingLeft: 18, listStyle: "none" }}>
              {ehv.konkret_helyzetek.map((it, i) => (
                <li key={i} style={{
                  position: "relative", paddingLeft: 16,
                  marginBottom: 10, lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 15,
                }}>
                  <span style={{
                    position: "absolute", left: 0, top: 10,
                    width: 6, height: 6, borderRadius: 999, background: V2_PURPLE,
                  }} />
                  {toText(it)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* === 04 · FÁJDALOM === */}
      {Array.isArray(t5p.elemek) && t5p.elemek.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="v2-sec-fajdalom">
          <V2SectionHeader num="04" label="FÁJDALOM" title="Top 5 legfontosabb probléma" />
          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            {t5p.elemek.map((it, i) => (
              <ProblemDesireCard key={i} idx={i} item={it} accent="red" kind="problem" />
            ))}
          </div>
          {t5p.kozos_minta && <KozosMinta value={t5p.kozos_minta} accent="red" />}
        </section>
      )}

      {/* === 05 · VÁGY === */}
      {Array.isArray(t5v.elemek) && t5v.elemek.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="v2-sec-vagy">
          <V2SectionHeader num="05" label="VÁGY" title="Top 5 legfontosabb vágy" />
          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            {t5v.elemek.map((it, i) => (
              <ProblemDesireCard key={i} idx={i} item={it} accent="green" kind="desire" />
            ))}
          </div>
          {t5v.kozos_minta && <KozosMinta value={t5v.kozos_minta} accent="green" />}
        </section>
      )}

      {/* === 06 · NAPLÓBEJEGYZÉS === */}
      {naplo && (
        <section style={{ marginBottom: 48 }} data-testid="v2-sec-naplo">
          <V2SectionHeader num="06" label="NAPLÓBEJEGYZÉS" title="Naplóbejegyzés az avatar fejéből" />
          <div style={{
            background: "linear-gradient(135deg, rgba(255,255,255,0.75), rgba(247,243,235,0.5))",
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: 14,
            padding: "32px 36px",
            position: "relative",
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          }}>
            <div style={{
              position: "absolute", top: 16, left: 24,
              fontSize: 72, color: V2_PURPLE, opacity: 0.18,
              fontFamily: "Georgia, serif", lineHeight: 1, pointerEvents: "none",
            }}>“</div>
            <div style={{
              whiteSpace: "pre-wrap",
              color: V2_INK_SOFT,
              fontSize: 15,
              lineHeight: 1.75,
              fontStyle: "italic",
              position: "relative",
            }}>
              {toText(naplo)}
            </div>
          </div>
        </section>
      )}

      {/* ====================================================================
           KIEGÉSZÍTŐ FELISMERÉSEK — 13 lenyitható szekció (LÉPÉS 2)
           ==================================================================== */}
      <div style={{
        borderTop: "1px solid rgba(0,0,0,0.10)",
        marginTop: 24, paddingTop: 40,
      }} data-testid="v2-supp">
        <div style={{ marginBottom: 28 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: V2_PURPLE,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 14,
          }}>Kiegészítő felismerések</div>
          <p style={{ margin: 0, color: V2_MUTED, fontSize: 15, lineHeight: 1.65 }}>
            Mélyebb rétegek, amik kiegészítik az Ideális Ügyfélprofilodat.
          </p>
        </div>

        {/* Loading state — supp is being generated in the background */}
        {!hasSupp && suppStatus === "loading" && (
          <div style={{
            padding: "22px 28px",
            background: V2_PURPLE_BG_SOFT,
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14, color: V2_PURPLE_DEEP, fontSize: 15,
            display: "flex", alignItems: "center", gap: 14,
          }}>
            <span style={{
              width: 18, height: 18, borderRadius: 999,
              border: `2px solid ${V2_PURPLE_BORDER_SOFT}`,
              borderTopColor: V2_PURPLE,
              animation: "v2spin 1s linear infinite",
              display: "inline-block", flexShrink: 0,
            }} />
            <span>A kiegészítő felismerések háttérben generálódnak… (kb. 30-60 mp)</span>
            <style>{`@keyframes v2spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Error state — show retry link */}
        {suppStatus === "error" && (
          <div style={{
            padding: "20px 24px",
            background: "rgba(214,128,128,0.06)",
            border: "1px solid rgba(214,128,128,0.20)",
            borderRadius: 14, color: "#9b3838",
            fontSize: 15, lineHeight: 1.6,
          }}>
            <div style={{ marginBottom: 8 }}>
              A kiegészítők generálása sikertelen{suppError ? ` (${suppError})` : ""}.
            </div>
            {onSuppRetry && (
              <button
                type="button"
                onClick={onSuppRetry}
                style={{
                  background: "transparent", border: "none",
                  color: V2_PURPLE_DEEP, fontWeight: 600,
                  fontSize: 15, cursor: "pointer", padding: 0,
                  textDecoration: "underline",
                }}
                data-testid="v2-supp-retry"
              >
                Generáld újra a kiegészítőket →
              </button>
            )}
          </div>
        )}

        {/* Done state — render the 13 collapsible sections */}
        {hasSupp && (
          <>
            {bm && (
              <V2Collapsible num="07" label="MONOLÓG" title="Belső monológ">
                <V2List label="Amikor elakad" items={bm.amikor_elakad} />
                <V2List label="Amikor összehasonlítja magát másokkal" items={bm.amikor_osszehasonlitja} />
                <V2List label="Amikor halogat" items={bm.amikor_halogat} />
                <V2List label="Amikor segítségkérésen gondolkodik" items={bm.amikor_segitseget_megfontol} />
                <V2List label="Amikor reményt érez" items={bm.amikor_remenyt_erez} />
                <V2List label="Amikor fél a változástól" items={bm.amikor_fel_a_valtozastol} />
              </V2Collapsible>
            )}
            {usz && (
              <V2Collapsible num="08" label="NYELV" title="Ügyfélnyelv-szótár">
                <V2List label="Gyakran használt szavak és kifejezések" items={usz.gyakori_szavak_kifejezesek} />
                <V2List label="Tipikus mondatok" items={usz.tipikus_mondatok} />
                <V2List label="Kerülendő szavak" items={usz.kerulendo_szavak} />
                <V2List label="Szavak, amelyekre jól reagál" items={usz.amire_jol_reagal_szavak} />
                {Array.isArray(usz.fordito_tablazat) && usz.fordito_tablazat.length > 0 && (
                  <>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                      letterSpacing: "0.10em", textTransform: "uppercase",
                      marginTop: 18, marginBottom: 10,
                    }}>Szakértői → Avatar nyelv fordítás</div>
                    <V2Table
                      rows={usz.fordito_tablazat}
                      columns={[
                        { key: "szakertoi", label: "Szakértői" },
                        { key: "avatar", label: "Ahogy az avatar mondaná" },
                      ]}
                    />
                  </>
                )}
              </V2Collapsible>
            )}
            {flk && (
              <V2Collapsible num="09" label="ELLENÁLLÁS" title="Félelmek, kifogások és rejtett ellenállások">
                <V2List label="Mély félelmek" items={flk.mely_felelmek} />
                <V2List label="Döntési kifogások" items={flk.dontesi_kifogasok} />
                {Array.isArray(flk.kifogas_mogott_tablazat) && flk.kifogas_mogott_tablazat.length > 0 && (
                  <>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                      letterSpacing: "0.10em", textTransform: "uppercase",
                      marginTop: 18, marginBottom: 10,
                    }}>Mi van a kifogás mögött?</div>
                    <V2Table
                      rows={flk.kifogas_mogott_tablazat}
                      columns={[
                        { key: "kifogas", label: "Kifogás" },
                        { key: "valodi_felelem", label: "Valódi félelem" },
                        { key: "mire_van_szuksege", label: "Mire van szüksége" },
                        { key: "milyen_kommunikacio_oldhatja", label: "Mi oldja" },
                        { key: "milyen_kommunikacio_zarna_be", label: "Mi zárja be" },
                      ]}
                    />
                  </>
                )}
              </V2Collapsible>
            )}
            {dp && (
              <V2Collapsible num="10" label="DÖNTÉS" title="Döntési pszichológia">
                <V2List label="Mikor kezd megoldást keresni" items={dp.mikor_kezd_megoldast_keresni} />
                <V2List label="Mi billenti át" items={dp.mi_billenti_at} />
                <V2List label="Mitől bízik meg" items={dp.mitol_bizik_meg} />
                <V2List label="Mitől veszti el a bizalmat" items={dp.mitol_veszti_el_bizalmat} />
                <V2List label="Mitől érzi biztonságosnak a következő lépést" items={dp.mitol_erzi_biztonsagosnak_a_kovetkezo_lepest} />
              </V2Collapsible>
            )}
            {Array.isArray(mvi) && mvi.length > 0 && (
              <V2Collapsible num="11" label="SZÜKSÉGLET" title="Mire van igazán szüksége?">
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {mvi.map((c, i) => (
                    <div key={i} style={{
                      background: V2_PURPLE_BG_SOFT,
                      border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
                      borderRadius: 12, padding: "16px 20px",
                    }}>
                      <div style={{ marginBottom: 6, fontSize: 15, lineHeight: 1.6 }}>
                        <strong style={{ color: V2_INK }}>Azt mondja: </strong>{toText(c.azt_mondja_ezt_akarja)}
                      </div>
                      <div style={{ marginBottom: 6, fontSize: 15, lineHeight: 1.6 }}>
                        <strong style={{ color: V2_INK }}>Valójában: </strong>{toText(c.valojaban_szuksege_van)}
                      </div>
                      {c.miert_fontos && (
                        <div style={{ color: V2_MUTED, fontSize: 14.5, fontStyle: "italic", lineHeight: 1.55 }}>
                          {toText(c.miert_fontos)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </V2Collapsible>
            )}
            {ke && (
              <V2Collapsible num="12" label="HANGNEM" title="Kommunikációs érzékenységek">
                <V2List label="Erre jól reagál" items={ke.erre_jol_reagal} />
                <V2List label="Erre rosszul reagál" items={ke.erre_rosszul_reagal} />
                {ke.ajanlott_hangnem && (
                  <div style={{ marginTop: 18 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                      letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 10,
                    }}>Ajánlott hangnem</div>
                    <ul style={{ margin: 0, paddingLeft: 18, listStyle: "none" }}>
                      {Object.entries(ke.ajanlott_hangnem).filter(([, v]) => v).map(([k, v]) => (
                        <li key={k} style={{
                          position: "relative", paddingLeft: 16,
                          marginBottom: 8, lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 15,
                        }}>
                          <span style={{
                            position: "absolute", left: 0, top: 10,
                            width: 6, height: 6, borderRadius: 999, background: V2_PURPLE,
                          }} />
                          <strong style={{ color: V2_INK }}>{k.replace(/_/g, " ")}: </strong>{toText(v)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <V2List label="Jó példamondatok" items={ke.jo_pelda_mondatok} />
                <V2List label="Kerülendő példamondatok" items={ke.kerulendo_pelda_mondatok} />
              </V2Collapsible>
            )}
            {mpm && (
              <V2Collapsible num="13" label="PRÓBÁLKOZÁS" title="Mit próbált már, és miért nem működött?">
                {Array.isArray(mpm.tablazat) && (
                  <V2Table
                    rows={mpm.tablazat}
                    columns={[
                      { key: "probalkozas", label: "Korábbi próbálkozás" },
                      { key: "miert_probalta", label: "Miért próbálta" },
                      { key: "miert_nem_mukodott", label: "Miért nem működött" },
                      { key: "milyen_erzest_hagyott", label: "Milyen érzést hagyott" },
                    ]}
                  />
                )}
                {mpm.osszegzes && (
                  <p style={{ marginTop: 16, color: V2_INK_SOFT, lineHeight: 1.65, fontSize: 16 }}>
                    {toText(mpm.osszegzes)}
                  </p>
                )}
              </V2Collapsible>
            )}
            {tkp && (
              <V2Collapsible num="14" label="KIVÁLTÓ PONTOK" title="Tartalmi kiváltó pontok">
                <V2List label="Milyen témákra figyel fel" items={tkp.milyen_temakra_figyel_fel} />
                <V2List label="Milyen helyzetekben ismer magára" items={tkp.milyen_helyzetekben_ismer_magara} />
                <V2List label="Milyen mondatok állítják meg" items={tkp.milyen_mondatok_allitjak_meg} />
                <V2List label="Milyen tévhitek érintik" items={tkp.milyen_tevhitek_erintik} />
                <V2List label="Milyen kontrasztokra reagál" items={tkp.milyen_kontrasztokra_reagal} />
              </V2Collapsible>
            )}
            {Array.isArray(ki) && ki.length > 0 && (
              <V2Collapsible num="15" label="INSPIRÁCIÓK" title="Kontextuális inspirációk és gondolati világ">
                <ul style={{ margin: 0, paddingLeft: 18, listStyle: "none" }}>
                  {ki.map((it, i) => (
                    <li key={i} style={{
                      position: "relative", paddingLeft: 16,
                      marginBottom: 12, lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 15,
                    }}>
                      <span style={{
                        position: "absolute", left: 0, top: 10,
                        width: 6, height: 6, borderRadius: 999, background: V2_PURPLE,
                      }} />
                      <strong style={{ color: V2_INK }}>{toText(it.iranay_vagy_konyv)}: </strong>
                      {toText(it.miert_releváns)}
                    </li>
                  ))}
                </ul>
              </V2Collapsible>
            )}
            {med && (
              <V2Collapsible num="16" label="FIGYELEM" title="Médiafogyasztás és figyelmi környezet">
                {/* Hol találkozunk vele — platform+miért+példák kártyák. */}
                {Array.isArray(med.hol_talalkozunk) && med.hol_talalkozunk.length > 0 && (
                  <>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                      letterSpacing: "0.10em", textTransform: "uppercase",
                      marginTop: 8, marginBottom: 10,
                    }}>Hol találkozunk vele</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 18 }}>
                      {med.hol_talalkozunk.map((p, i) => (
                        <div key={i} style={{
                          background: V2_PURPLE_BG_SOFT,
                          border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
                          borderRadius: 12, padding: "16px 20px",
                        }}>
                          <div style={{ marginBottom: 6, fontSize: 15, lineHeight: 1.6 }}>
                            <strong style={{ color: V2_INK }}>{toText(p.platform)}</strong>
                            {p.miert_ott ? <> — <span style={{ color: V2_INK_SOFT }}>{toText(p.miert_ott)}</span></> : null}
                          </div>
                          {Array.isArray(p.peldak) && p.peldak.length > 0 && (
                            <ul style={{ margin: "6px 0 0 20px", padding: 0, color: V2_INK_SOFT, fontSize: 15, lineHeight: 1.6 }}>
                              {p.peldak.map((ex, j) => <li key={j}>{toText(ex)}</li>)}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Kiket és miket figyelhet — bucketes struktúra */}
                {med.kiket_es_miket_figyelhet && (
                  <>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                      letterSpacing: "0.10em", textTransform: "uppercase",
                      marginTop: 18, marginBottom: 10,
                    }}>Kiket és miket figyelhet</div>
                    {Array.isArray(med.kiket_es_miket_figyelhet.szakertok_gurun_szerzok) && med.kiket_es_miket_figyelhet.szakertok_gurun_szerzok.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: V2_INK, marginBottom: 6 }}>Szakértők, guruk, szerzők</div>
                        <ul style={{ margin: "0 0 0 20px", padding: 0, color: V2_INK_SOFT, fontSize: 15, lineHeight: 1.65 }}>
                          {med.kiket_es_miket_figyelhet.szakertok_gurun_szerzok.map((s, i) => (
                            <li key={i} style={{ marginBottom: 4 }}>
                              <strong style={{ color: V2_INK }}>{toText(s.nev_vagy_archetypus)}</strong>
                              {s.miert_relevans ? ` — ${toText(s.miert_relevans)}` : ""}
                              {s.valoszinuseg ? ` (${toText(s.valoszinuseg)})` : ""}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <V2List label="Podcastok" items={med.kiket_es_miket_figyelhet.podcastok} />
                    <V2List label="YouTube-csatornák" items={med.kiket_es_miket_figyelhet.youtube_csatornak} />
                    <V2List label="Könyvek, szerzők" items={med.kiket_es_miket_figyelhet.konyvek_szerzok} />
                    <V2List label="Hírlevelek, Substackek" items={med.kiket_es_miket_figyelhet.hirlevek_substackek} />
                    <V2List label="Online közösségek" items={med.kiket_es_miket_figyelhet.online_kozossegek} />
                    <V2List label="Ismert módszerek, kurzusok" items={med.kiket_es_miket_figyelhet.ismert_modszerek_kurzusok} />
                    <V2List label="Konferenciák, események" items={med.kiket_es_miket_figyelhet.konferenciak_events} />
                  </>
                )}

                {/* Keresési viselkedés */}
                {med.keresesi_viselkedes && (
                  <>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                      letterSpacing: "0.10em", textTransform: "uppercase",
                      marginTop: 18, marginBottom: 10,
                    }}>Keresési viselkedés</div>
                    <V2List label="Google keresések" items={med.keresesi_viselkedes.google_keresesek} />
                    <V2List label="YouTube keresések" items={med.keresesi_viselkedes.youtube_keresesek} />
                    <V2List label="ChatGPT kérdések" items={med.keresesi_viselkedes.chatgpt_kerdesek} />
                    <V2List label="Podcast app keresések" items={med.keresesi_viselkedes.podcast_app_keresesek} />
                    <V2List label="Közösségi keresések" items={med.keresesi_viselkedes.kozossegi_kereso} />
                  </>
                )}

                <V2List label="Milyen témák érdeklik" items={med.milyen_temak_erdeklik} />

                {/* Formátumpreferenciák — kártyák, ugyanaz a stílus mint a hol_talalkozunk */}
                {Array.isArray(med.formatumpreferenciak) && med.formatumpreferenciak.length > 0 && (
                  <>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                      letterSpacing: "0.10em", textTransform: "uppercase",
                      marginTop: 18, marginBottom: 10,
                    }}>Formátumpreferenciák</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 18 }}>
                      {med.formatumpreferenciak.map((f, i) => (
                        <div key={i} style={{
                          background: V2_PURPLE_BG_SOFT,
                          border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
                          borderRadius: 12, padding: "14px 18px",
                        }}>
                          <div style={{ fontSize: 15, lineHeight: 1.6 }}>
                            <strong style={{ color: V2_INK }}>{toText(f.formatum)}</strong>
                            {f.miert_mukodik_nala ? <> — <span style={{ color: V2_INK_SOFT }}>{toText(f.miert_mukodik_nala)}</span></> : null}
                          </div>
                          {f.ideális_hossz_vagy_forma && (
                            <div style={{ marginTop: 4, fontSize: 14.5, color: V2_MUTED, fontStyle: "italic", lineHeight: 1.55 }}>
                              Ideális forma: {toText(f.ideális_hossz_vagy_forma)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Fogyasztási helyzet — egyszerű bekezdéses stílus, mint más szekciók */}
                {med.fogyasztasi_helyzet && (
                  <>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                      letterSpacing: "0.10em", textTransform: "uppercase",
                      marginTop: 18, marginBottom: 10,
                    }}>Fogyasztási helyzet</div>
                    {med.fogyasztasi_helyzet.mikor_fogyaszt_leggyakrabban && (
                      <p style={{ margin: "8px 0", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
                        <strong style={{ color: V2_INK }}>Mikor: </strong>{toText(med.fogyasztasi_helyzet.mikor_fogyaszt_leggyakrabban)}
                      </p>
                    )}
                    {med.fogyasztasi_helyzet.milyen_eszkozon && (
                      <p style={{ margin: "8px 0", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
                        <strong style={{ color: V2_INK }}>Eszköz: </strong>{toText(med.fogyasztasi_helyzet.milyen_eszkozon)}
                      </p>
                    )}
                    {med.fogyasztasi_helyzet.milyen_elethelyzetben && (
                      <p style={{ margin: "8px 0", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
                        <strong style={{ color: V2_INK }}>Élethelyzet: </strong>{toText(med.fogyasztasi_helyzet.milyen_elethelyzetben)}
                      </p>
                    )}
                    {med.fogyasztasi_helyzet.milyen_problematudat_mellett && (
                      <p style={{ margin: "8px 0", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
                        <strong style={{ color: V2_INK }}>Problématudat: </strong>{toText(med.fogyasztasi_helyzet.milyen_problematudat_mellett)}
                      </p>
                    )}
                    {med.fogyasztasi_helyzet.vasarlasi_dontesi_pont && (
                      <p style={{ margin: "8px 0", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
                        <strong style={{ color: V2_INK }}>Vásárlási döntési pont: </strong>{toText(med.fogyasztasi_helyzet.vasarlasi_dontesi_pont)}
                      </p>
                    )}
                  </>
                )}

                {/* Legacy / kompatibilitási mezők — ugyanaz a V2List stílus mint máshol */}
                <V2List label="Felületek" items={med.feluletek} />
                <V2List label="Tartalomtípusok" items={med.tartalomtipusok} />
                <V2List label="Preferált formátumok (rövid lista)" items={med.preferalt_formatumok} />
                {med.mikor_fogyaszt && (
                  <p style={{ margin: "10px 0", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
                    <strong style={{ color: V2_INK }}>Mikor fogyaszt: </strong>{toText(med.mikor_fogyaszt)}
                  </p>
                )}
                {med.milyen_hosszu_tartalmat_bir && (
                  <p style={{ margin: "10px 0", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
                    <strong style={{ color: V2_INK }}>Milyen hosszút bír el: </strong>{toText(med.milyen_hosszu_tartalmat_bir)}
                  </p>
                )}
                <V2List label="Mire kattint" items={med.mire_kattint} />
                <V2List label="Mire görget tovább" items={med.mire_gorget_tovabb} />
                {med.milyen_csali_kenyelmes && (
                  <p style={{ margin: "10px 0", lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 16 }}>
                    <strong style={{ color: V2_INK }}>Milyen csali kényelmes: </strong>{toText(med.milyen_csali_kenyelmes)}
                  </p>
                )}
              </V2Collapsible>
            )}
            {Array.isArray(rftab) && rftab.length > 0 && (
              <V2Collapsible num="17" label="FORDÍTÓTÁR" title="Részletes szakértői → avatar nyelv fordítótár">
                <V2Table
                  rows={rftab}
                  columns={[
                    { key: "szakertoi", label: "Szakértői" },
                    { key: "avatar", label: "Ahogy az avatar mondaná" },
                  ]}
                />
              </V2Collapsible>
            )}
            {kta && (
              <V2Collapsible num="18" label="TOVÁBBI ALAPANYAGOK" title="Kiegészítő tartalmi alapanyagok">
                <V2List label="További élethelyzetek" items={kta.tovabbi_elethelyzetek} />
                <V2List label="További belső konfliktusok" items={kta.tovabbi_belso_konfliktusok} />
                <V2List label="További tévhitek" items={kta.tovabbi_tevhitek} />
                <V2List label="További érzékeny pontok" items={kta.tovabbi_erzekeny_pontok} />
              </V2Collapsible>
            )}
            {/* === 19 · IDENTITÁS — additív, a riport végén, csukva === */}
            {identitas && Object.keys(identitas).length > 0 && !identitas._error && (
              <IdentitasSection identitas={identitas} />
            )}
            {/* === 20 · TRENDEK / PIACI RADAR — additív, web-keresésen alapul, 30 nap után elavulhat. === */}
            {trendek && Object.keys(trendek).length > 0 && !trendek._error && (
              <TrendekSection trendek={trendek} bp={bp} />
            )}
            {ta && Object.keys(ta).length > 0 && (
              <V2Collapsible
                num="21" label="TARTALMI ALAPÖSSZEFOGLALÓ"
                title="Injektálható Avatar-összefoglaló (downstream ügynökök mag)"
              >
                <div style={{ fontSize: 14.5, color: V2_MUTED, fontStyle: "italic", marginBottom: 18 }}>
                  Az Avatar stratégiai esszenciája — önmagában elég egy downstream tartalomgeneráló ügynöknek jó marketingkommunikációhoz.
                </div>
                {ta.avatar_roviden && <p style={{ marginTop: 0, lineHeight: 1.65, fontSize: 15 }}>{toText(ta.avatar_roviden)}</p>}

                {/* KI Ő MOST */}
                {ta.ki_o_most && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: V2_MUTED, marginBottom: 6 }}>Ki ő most</div>
                    {ta.ki_o_most.kicsoda && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Kicsoda: </strong>{toText(ta.ki_o_most.kicsoda)}</p>}
                    {ta.ki_o_most.tipikus_nap && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Tipikus napja: </strong>{toText(ta.ki_o_most.tipikus_nap)}</p>}
                    {ta.ki_o_most.napi_ritmus && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Napi ritmus: </strong>{toText(ta.ki_o_most.napi_ritmus)}</p>}
                    <V2List label="Szerepek és felelősségek" items={ta.ki_o_most.szerepek_es_felelossegek} />
                    {ta.ki_o_most.mikor_talalkozik_problemaval && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Mikor találkozik a problémával: </strong>{toText(ta.ki_o_most.mikor_talalkozik_problemaval)}</p>}
                  </div>
                )}

                {/* PROBLÉMA ÉS FESZÜLTSÉG */}
                {ta.problema_es_feszultseg && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: V2_MUTED, marginBottom: 6 }}>Probléma és feszültség</div>
                    {ta.problema_es_feszultseg.fo_problema && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Fő probléma: </strong>{toText(ta.problema_es_feszultseg.fo_problema)}</p>}
                    <V2List label="Mi frusztrálja leginkább" items={ta.problema_es_feszultseg.mi_frusztralja_leginkabb} />
                    <V2List label="Mitől fél" items={ta.problema_es_feszultseg.mitol_fel} />
                    <V2List label="Mit veszít ha semmi nem változik" items={ta.problema_es_feszultseg.mit_veszit_ha_semmi_nem_valtozik} />
                    <V2List label="Mit próbált már" items={ta.problema_es_feszultseg.mit_probalt_mar} />
                    <V2List label="Miért nem működött" items={ta.problema_es_feszultseg.miert_nem_mukodott} />
                    <V2List label="Mi tartja vissza" items={ta.problema_es_feszultseg.mi_tartja_vissza} />
                  </div>
                )}

                {/* VÁGYOTT EREDMÉNY */}
                {ta.vagyott_eredmeny && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: V2_MUTED, marginBottom: 6 }}>Vágyott eredmény</div>
                    {ta.vagyott_eredmeny.konkret_cel && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Konkrét cél: </strong>{toText(ta.vagyott_eredmeny.konkret_cel)}</p>}
                    <V2List label="Kézzelfogható változás" items={ta.vagyott_eredmeny.kezzelfoghato_valtozas} />
                    {ta.vagyott_eredmeny.hogyan_akar_erezni_maga && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Hogyan akar érezni: </strong>{toText(ta.vagyott_eredmeny.hogyan_akar_erezni_maga)}</p>}
                    {ta.vagyott_eredmeny.milyen_eletet_szeretne_helyette && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Milyen életet szeretne helyette: </strong>{toText(ta.vagyott_eredmeny.milyen_eletet_szeretne_helyette)}</p>}
                  </div>
                )}

                {/* IDENTITÁSVÁLTÁS — kiemelt */}
                {ta.identitasvaltas && (
                  <div style={{ marginTop: 16, padding: "12px 16px", background: "rgba(94,80,247,0.06)", border: "1px solid rgba(94,80,247,0.20)", borderRadius: 12 }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#a89bff", marginBottom: 8, fontWeight: 700 }}>Identitásváltás — kritikus</div>
                    {ta.identitasvaltas.mostani_en && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Mostani én: </strong>{toText(ta.identitasvaltas.mostani_en)}</p>}
                    <V2List label="Régi identitás, amit maga mögött hagyna" items={ta.identitasvaltas.regi_identitas_amit_maga_mogott_hagyna} />
                    {ta.identitasvaltas.vagyott_en && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Vágyott én: </strong>{toText(ta.identitasvaltas.vagyott_en)}</p>}
                    <V2List label="Hogyan gondolkodna a jövőbeli énje" items={ta.identitasvaltas.hogyan_gondolkodna_jovobeli_enje} />
                    <V2List label="Hogyan viselkedne" items={ta.identitasvaltas.hogyan_viselkedne} />
                    <V2List label="Hogyan döntene" items={ta.identitasvaltas.hogyan_dontene} />
                    {ta.identitasvaltas.kulcs_transzformacio && <p style={{ margin: "10px 0 2px", lineHeight: 1.65, color: V2_INK, fontWeight: 600 }}>▸ {toText(ta.identitasvaltas.kulcs_transzformacio)}</p>}
                  </div>
                )}

                {/* VÁSÁRLÁSI DÖNTÉS */}
                {ta.vasarlasi_dontes && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: V2_MUTED, marginBottom: 6 }}>Vásárlási döntés</div>
                    {ta.vasarlasi_dontes.hogyan_hoz_dontest && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Hogyan hoz döntést: </strong>{toText(ta.vasarlasi_dontes.hogyan_hoz_dontest)}</p>}
                    {ta.vasarlasi_dontes.mikor_kezd_aktivan_keresni && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Mikor kezd keresni: </strong>{toText(ta.vasarlasi_dontes.mikor_kezd_aktivan_keresni)}</p>}
                    {ta.vasarlasi_dontes.mikor_hajlando_fizetni && <p style={{ margin: "4px 0", lineHeight: 1.6, color: V2_INK_SOFT }}><strong style={{ color: V2_INK }}>Mikor fizet: </strong>{toText(ta.vasarlasi_dontes.mikor_hajlando_fizetni)}</p>}
                    <V2List label="Sürgősségi triggerek" items={ta.vasarlasi_dontes.surgossegi_triggerek} />
                    <V2List label="Fő kifogások" items={ta.vasarlasi_dontes.fo_kifogasok} />
                    <V2List label="Érzelmi mozgatórugók" items={ta.vasarlasi_dontes.erzelmi_mozgatorugok} />
                    <V2List label="Racionális mozgatórugók" items={ta.vasarlasi_dontes.racionalis_mozgatorugok} />
                    <V2List label="Milyen bizonyíték kell" items={ta.vasarlasi_dontes.milyen_bizonyitek_kell} />
                    <V2List label="Mi épít bizalmat" items={ta.vasarlasi_dontes.mi_epit_bizalmat} />
                    <V2List label="Kockázatcsökkentők" items={ta.vasarlasi_dontes.kockazatcsokkentok} />
                    {ta.vasarlasi_dontes.vegso_indito_ok && <p style={{ margin: "6px 0", lineHeight: 1.6, color: V2_INK, fontWeight: 600 }}>▸ Végső indító ok: {toText(ta.vasarlasi_dontes.vegso_indito_ok)}</p>}
                  </div>
                )}

                {/* KOMMUNIKÁCIÓS KAPCSOLÓDÁS */}
                {ta.kommunikacios_kapcsolodas && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: V2_MUTED, marginBottom: 6 }}>Kommunikációs kapcsolódás</div>
                    <V2List label="Mire reagál" items={ta.kommunikacios_kapcsolodas.mire_reagal} />
                    <V2List label="Problémaszögek, amiben ráismer" items={ta.kommunikacios_kapcsolodas.problema_szogek_amiben_ramert} />
                    <V2List label="Vágykép-szögek, amiket rezonál" items={ta.kommunikacios_kapcsolodas.vagykep_szogek_amiket_rezonál} />
                    <V2List label="Erős tartalmi szögek" items={ta.kommunikacios_kapcsolodas.eros_tartalmi_szogek} />
                    <V2List label="Figyelemmegszakító üzenetek" items={ta.kommunikacios_kapcsolodas.figyelemmegszakito_uzenetek} />
                  </div>
                )}

                {/* MÉDIAFOGYASZTÁS LÉNYEGE */}
                {ta.mediafogyasztas_lenyege && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: V2_MUTED, marginBottom: 6 }}>Médiafogyasztás lényege</div>
                    <V2List label="Hol érhető el" items={ta.mediafogyasztas_lenyege.hol_erheto_el} />
                    <V2List label="Milyen tartalmakat fogyaszt" items={ta.mediafogyasztas_lenyege.milyen_tartalmakat_fogyaszt} />
                    <V2List label="Kikre / mire figyel" items={ta.mediafogyasztas_lenyege.kikre_mire_figyel} />
                    <V2List label="Milyen formátumok működnek" items={ta.mediafogyasztas_lenyege.milyen_formatumok_mukodnek} />
                  </div>
                )}

                {/* TRENDEK LÉNYEGE — post-hoc szerver-oldali fill */}
                {ta.trendek_lenyege && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: V2_MUTED, marginBottom: 6 }}>Trendek / piaci radar lényege</div>
                    <V2List label="Friss témák" items={ta.trendek_lenyege.friss_temak} />
                    <V2List label="Új félelmek / vágyak" items={ta.trendek_lenyege.uj_felelmek_vagyak} />
                    <V2List label="Aktuális ellenállások" items={ta.trendek_lenyege.aktualis_ellenallasok} />
                    <V2List label="Piaci változások" items={ta.trendek_lenyege.piaci_valtozasok} />
                    <V2List label="Friss nyelv / narratívák" items={ta.trendek_lenyege.friss_nyelv_narrativak} />
                  </div>
                )}

                {/* Legacy essence blokkok (downstream trend-radar ezekre hivatkozik). */}
                <div style={{ marginTop: 18, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 12 }}>
                  <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: V2_MUTED, marginBottom: 6 }}>Esszencia mezők (downstream mag)</div>
                  <V2List label="Top 5 probléma esszencia" items={ta.top_5_problema_essence} />
                  <V2List label="Top 5 vágy esszencia" items={ta.top_5_vagy_essence} />
                  <V2List label="Belső monológ esszencia" items={ta.belso_monolog_essence} />
                  {ta.ugyfelnyelv_essence && (
                    <>
                      <V2List label="Gyakori szavak" items={ta.ugyfelnyelv_essence.gyakori_szavak} />
                      <V2List label="Kerülendő szavak" items={ta.ugyfelnyelv_essence.kerulendo_szavak} />
                    </>
                  )}
                  <V2List label="Fő félelmek és kifogások" items={ta.fo_felelmek_kifogasok} />
                  <V2List label="Döntési pszichológia esszencia" items={ta.dontesi_pszichologia_essence} />
                  <V2List label="Mire van igazán szüksége" items={ta.mire_van_igazan_szuksege} />
                  <V2List label="Kommunikációs érzékenységek esszencia" items={ta.kommunikacios_erzekenysegek_essence} />
                  <V2List label="Kerülendő kommunikáció" items={ta.kerulendo_kommunikacio} />
                </div>
              </V2Collapsible>
            )}
            {/* === 22 · FORRÁS ÉS BŐVÍTÉS — a riport ABSZOLÚT vége === */}
            <SourceExpansionSection bp={bp} fm={fm} />
          </>
        )}
      </div>
    </div>
  );
}

function IdentitasSection({ identitas }) {
  const id = identitas || {};
  const vagyott = toText(id.vagyott_identitas);
  const beszeljenek = Array.isArray(id.hogyan_beszeljenek_rola) ? id.hogyan_beszeljenek_rola : [];
  const antiIdent = Array.isArray(id.anti_identitas) ? id.anti_identitas : [];
  const teAzAz = Array.isArray(id.te_az_az_ember_vagy_mondatok) ? id.te_az_az_ember_vagy_mondatok : [];
  const jelenlegi = Array.isArray(id.jelenlegi_onkep) ? id.jelenlegi_onkep : [];
  const vesztes = Array.isArray(id.identitasvesztes_ha_nem_valtozik) ? id.identitasvesztes_ha_nem_valtozik : [];
  const cel = Array.isArray(id.cel_identitas) ? id.cel_identitas : [];
  const alomnap = toText(id.alomnap_alomforgatokonyv);

  return (
    <V2Collapsible
      num="19" label="IDENTITÁS"
      title="Identitás — kivé akar válni, hogyan akarja, hogy lássák"
    >
      <div style={{ fontSize: 14.5, color: V2_MUTED, fontStyle: "italic", marginBottom: 18 }}>
        Koherens önkép-réteg, egy helyen. A Vágy MIT akar elérni, az Identitás KIVÉ akar válni.
      </div>
      {vagyott && (
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 12, fontWeight: 600, color: V2_PURPLE,
            letterSpacing: "0.08em", textTransform: "uppercase",
            marginBottom: 8,
          }}>19.1 · Vágyott identitás</div>
          <p style={{ marginTop: 0, lineHeight: 1.65, fontSize: 15 }}>{vagyott}</p>
        </div>
      )}
      <V2List label="19.2 · Hogyan akarja, hogy beszéljenek róla" items={beszeljenek} />
      <V2List label="19.3 · Anti-identitás (milyen szerepből lép ki)" items={antiIdent} />
      <V2List label={'19.4 · „Te az az ember vagy, aki…" mondatok'} items={teAzAz} />
      <V2List label="19.5 · Jelenlegi önkép" items={jelenlegi} />
      <V2List label="19.6 · Identitásveszteség, ha nem változik" items={vesztes} />
      <V2List label="19.7 · Cél-identitás" items={cel} />
      {alomnap && (
        <div style={{ marginTop: 24 }}>
          <div style={{
            fontSize: 12, fontWeight: 600, color: V2_PURPLE,
            letterSpacing: "0.08em", textTransform: "uppercase",
            marginBottom: 12,
          }}>19.8 · Álomnap / álomforgatókönyv</div>
          {/* Napló-stílus: ugyanaz a vizuális kezelés, mint a 06 · Naplóbejegyzés:
              gradient háttér, lila idézőjel, italic, vallomásos folyó szöveg. */}
          <div style={{
            background: "linear-gradient(135deg, rgba(255,255,255,0.75), rgba(247,243,235,0.5))",
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: 14,
            padding: "32px 36px",
            position: "relative",
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          }}>
            <div style={{
              position: "absolute", top: 16, left: 24,
              fontSize: 72, color: V2_PURPLE, opacity: 0.18,
              fontFamily: "Georgia, serif", lineHeight: 1, pointerEvents: "none",
            }}>“</div>
            <div style={{
              whiteSpace: "pre-wrap",
              color: V2_INK_SOFT,
              fontSize: 15,
              lineHeight: 1.75,
              fontStyle: "italic",
              position: "relative",
            }}>
              {alomnap}
            </div>
          </div>
        </div>
      )}
    </V2Collapsible>
  );
}

function TrendekSection({ trendek, bp }) {
  const t = trendek || {};
  const fd = t.forras_es_datum || {};
  const datum = toText(fd.frissites_datuma);
  const platformok = Array.isArray(fd.platformok) ? fd.platformok : [];
  const keresoKifejezesek = Array.isArray(fd.kereso_kifejezesek) ? fd.kereso_kifejezesek : [];
  const forraslista = Array.isArray(fd.forraslista) ? fd.forraslista : [];
  const forroTemak = Array.isArray(t.forro_temak) ? t.forro_temak : [];
  const fajdalomMondatok = Array.isArray(t.fajdalom_mondatok) ? t.fajdalom_mondatok : [];
  const hookCimRadar = Array.isArray(t.hook_cim_radar) ? t.hook_cim_radar : [];
  const frissNyelv = Array.isArray(t.friss_nyelv_kulcsszavak) ? t.friss_nyelv_kulcsszavak : [];
  const aktualisEllenallasok = Array.isArray(t.aktualis_ellenallasok) ? t.aktualis_ellenallasok : [];

  // 30 napos elavulás-figyelmeztetést a backend írja be a forras_es_datum
  // blokkba (elavult: true) — a frontend csak megjeleníti, nem számol.
  const elavult = !!fd.elavult;

  return (
    <V2Collapsible
      num="20" label="TRENDEK / PIACI RADAR"
      title="Trendek és piaci radar (friss, webes adatból)"
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        {datum && <V2Pill>Frissítés · {datum}</V2Pill>}
        {platformok.length > 0 && <V2Pill>Forrás · {platformok.join(", ")}</V2Pill>}
        <V2Pill>Web search · Tavily</V2Pill>
      </div>
      {elavult && (
        <div style={{
          fontSize: 13, color: "#a64418",
          background: "rgba(255, 220, 200, 0.55)",
          border: "1px solid rgba(214, 120, 65, 0.35)",
          borderRadius: 10, padding: "10px 14px",
          marginBottom: 16, lineHeight: 1.5,
        }}>
          A trendadatok elavulhatnak (több mint 30 napja frissültek). Érdemes újra generálni az avatart a friss piaci radarért.
        </div>
      )}
      <div style={{ fontSize: 14.5, color: V2_MUTED, fontStyle: "italic", marginBottom: 18 }}>
        Frissülő piaci réteg, NEM az avatar stabil magja. Az avatar újragenerálásakor frissül.
      </div>
      {keresoKifejezesek.length > 0 && (
        <V2List label="20.1 · Használt keresőkifejezések" items={keresoKifejezesek} />
      )}
      {forraslista.length > 0 && (
        <V2List label="20.1 · Forráslista" items={forraslista} />
      )}
      <V2List label="20.2 · Aktuális forró témák" items={forroTemak} />
      <V2List label="20.3 · Trendi fájdalom-mondatok (amit az avatar most ténylegesen mondhatna)" items={fajdalomMondatok} />
      <V2List label="20.4 · Hook- és címradar (kategóriánként)" items={hookCimRadar} />
      <V2List label="20.5 · Friss nyelv és kulcsszavak" items={frissNyelv} />
      <V2List label="20.6 · Aktuális ellenállások" items={aktualisEllenallasok} />
    </V2Collapsible>
  );
}



function SourceExpansionSection({ bp, fm }) {
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const hianyzo = Array.isArray(fm?.hianyzo_informaciok) ? fm.hianyzo_informaciok : [];
  const [values, setValues] = React.useState(() => Object.fromEntries(hianyzo.map((h) => [h, ""])));

  // Keep values in sync if hianyzo changes (e.g. after regeneration).
  React.useEffect(() => {
    setValues((prev) => {
      const next = {};
      hianyzo.forEach((h) => { next[h] = prev[h] || ""; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hianyzo.join("|")]);

  const forrastypeLabel = (() => {
    const t = (fm?.forrastipus || "").toLowerCase();
    if (t.includes("weboldal") || t.includes("url")) return "weboldal";
    if (t.includes("dokum")) return "dokumentum";
    if (t.includes("kérdez") || t.includes("interview") || t.includes("felelek")) return "kérdezz-felelek";
    if (t.includes("leíráis") || t.includes("leiras")) return "felhasználói leírás";
    return "forrásanyag";
  })();

  const onExpand = bp?._on_expand_source;

  async function handleSubmit() {
    const trimmed = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, (v || "").trim()]).filter(([, v]) => v),
    );
    if (Object.keys(trimmed).length === 0) {
      // eslint-disable-next-line no-alert
      alert("Tölts ki legalább egy mezőt a bővítéshez.");
      return;
    }
    if (!onExpand) return;
    setSubmitting(true);
    try {
      await onExpand(trimmed);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  const hasMissing = hianyzo.length > 0;

  return (
    <V2Collapsible num="22" label="FORRÁS" title="Értékelés és bővítés">
      <div data-testid="v2-sec-forras">
      <p style={{ margin: "0 0 14px", color: V2_INK_SOFT, lineHeight: 1.65, fontSize: 15 }}>
        Ezt az Ideális Ügyfélprofilt <strong>{forrastypeLabel}</strong> alapján generáltuk. Erős kiindulópont,
        de minél több konkrét adatot adsz hozzá, annál pontosabb és mélyebb lesz minden, amit a többi ügynök
        ebből épít. A pontosítás bármikor megtörténhet — akár most, akár hetek múlva.
      </p>
      <V2PillRow>
        {fm?.forrasanyag_erossege && <V2Pill>Forrásanyag erőssége · {toText(fm.forrasanyag_erossege)}</V2Pill>}
        {fm?.avatar_tisztasaga && <V2Pill>Avatar tisztasága · {toText(fm.avatar_tisztasaga)}</V2Pill>}
      </V2PillRow>
      {hasMissing && (
        <div style={{ marginTop: 8, marginBottom: 24 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: V2_PURPLE,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 10,
          }}>Amivel mélyebb lenne</div>
          <ul style={{ margin: 0, paddingLeft: 18, listStyle: "none" }}>
            {hianyzo.map((h, i) => (
              <li key={i} style={{
                position: "relative", paddingLeft: 16,
                marginBottom: 8, lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 15,
              }}>
                <span style={{
                  position: "absolute", left: 0, top: 10,
                  width: 6, height: 6, borderRadius: 999, background: V2_PURPLE,
                }} />
                {toText(h)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={!hasMissing || !onExpand}
        style={{
          marginTop: 4,
          background: V2_PURPLE,
          color: "#ffffff",
          border: "none",
          borderRadius: 999,
          padding: "12px 22px",
          fontSize: 15,
          fontWeight: 600,
          cursor: hasMissing && onExpand ? "pointer" : "not-allowed",
          opacity: hasMissing && onExpand ? 1 : 0.5,
          letterSpacing: "0.01em",
          boxShadow: "0 2px 12px rgba(94,80,247,0.30)",
          transition: "transform 120ms ease, box-shadow 200ms ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 18px rgba(94,80,247,0.40)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(94,80,247,0.30)"; }}
        data-testid="v2-expand-source-btn"
      >
        + Forrásanyag bővítése
      </button>

      {open && (
        <SourceExpansionModal
          hianyzo={hianyzo}
          values={values}
          setValues={setValues}
          onClose={() => setOpen(false)}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      )}
      </div>
    </V2Collapsible>
  );
}

function SourceExpansionModal({ hianyzo, values, setValues, onClose, onSubmit, submitting }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(15, 14, 26, 0.55)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="v2-expand-source-modal"
    >
      <div style={{
        background: "#f7f3eb",
        borderRadius: 16,
        maxWidth: 640,
        width: "100%",
        maxHeight: "88vh",
        overflow: "auto",
        padding: "40px 44px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
        color: V2_INK,
        position: "relative",
      }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Bezárás"
          style={{
            position: "absolute", top: 16, right: 16,
            background: "transparent", border: "none",
            color: V2_MUTED, fontSize: 26, cursor: "pointer",
            width: 36, height: 36, borderRadius: 999,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            lineHeight: 1,
          }}
          data-testid="v2-expand-source-close"
        >×</button>
        <div style={{
          fontSize: 14, fontWeight: 500, color: V2_PURPLE,
          letterSpacing: "0.10em", textTransform: "uppercase",
          marginBottom: 12,
        }}>Forrásanyag bővítése</div>
        <div style={{
          fontSize: 25, fontWeight: 600, color: V2_INK, lineHeight: 1.2,
          marginBottom: 16, letterSpacing: "-0.012em",
        }}>Add hozzá, amit pontosabban tudsz.</div>
        <p style={{ margin: "0 0 24px", color: V2_INK_SOFT, lineHeight: 1.65, fontSize: 16 }}>
          Ahogy adatokat adsz hozzá, az Avatar újragenerálódik mélyebb és pontosabb formában. Egy mezőt sem kötelező kitölteni.
        </p>
        {hianyzo.map((h, i) => (
          <div key={i} style={{ marginBottom: 20 }}>
            <label style={{
              display: "block",
              fontSize: 14, fontWeight: 600, color: V2_INK,
              marginBottom: 8, lineHeight: 1.4,
            }}>{toText(h)}</label>
            <textarea
              value={values[h] || ""}
              onChange={(e) => setValues({ ...values, [h]: e.target.value })}
              rows={3}
              placeholder="Írd ide a konkrét adatot…"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "rgba(255,255,255,0.7)",
                fontSize: 15, lineHeight: 1.55, color: V2_INK_SOFT,
                fontFamily: "inherit",
                resize: "vertical",
                boxSizing: "border-box",
              }}
              data-testid={`v2-expand-input-${i}`}
            />
          </div>
        ))}
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          style={{
            marginTop: 8,
            width: "100%",
            background: V2_PURPLE,
            color: "#ffffff",
            border: "none",
            borderRadius: 12,
            padding: "14px 24px",
            fontSize: 15,
            fontWeight: 600,
            cursor: submitting ? "not-allowed" : "pointer",
            opacity: submitting ? 0.6 : 1,
            letterSpacing: "0.01em",
          }}
          data-testid="v2-expand-source-submit"
        >
          {submitting ? "Generálás indul…" : "Avatar újragenerálása a kiegészítésekkel"}
        </button>
      </div>
    </div>
  );
}

function ProblemDesireCard({ idx, item, accent, kind }) {
  const a = V2_ACCENT[accent];
  const fields = kind === "problem"
    ? [
        ["Röviden", item.mi_ez_roviden],
        ["Hétköznapokban", item.hetkoznapi_megnyilvanulas],
      ]
    : [
        ["Mit szeretne", item.mit_szeretne_elerni],
        ["Való életben", item.valo_eletben],
      ];
  const lateFields = kind === "problem"
    ? [
        ["Miért fáj", item.miert_faj_igazan],
        ["Mitől fél, ha nem változik", item.mitol_fel_ha_nem_valtozik],
      ]
    : [
        ["Miért fontos", item.miert_fontos_melyebben],
        ["Mitől fél, ha nem éri el", item.mitol_fel_ha_nem_eri_el],
      ];
  return (
    <article style={{
      background: "rgba(255,255,255,0.72)",
      border: `1px solid ${a.cardBorder}`,
      borderRadius: 14,
      padding: "32px 36px",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 18, marginBottom: 20,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 999,
          background: a.badgeBg, color: a.eyebrow,
          fontSize: 18, fontWeight: 700,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>{String(idx + 1).padStart(2, "0")}</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: a.eyebrow, lineHeight: 1.25 }}>
          {toText(item.nev)}
        </div>
      </div>
      {fields.map(([label, val]) => val && (
        <SubField key={label} label={label} value={val} accent={accent} />
      ))}
      {item.saját_szavaival && (
        <div style={{
          margin: "16px 0", padding: "16px 20px",
          borderLeft: `3px solid ${a.eyebrow}`,
          background: a.stripeBg,
          fontStyle: "italic", color: V2_INK_SOFT,
          lineHeight: 1.65, fontSize: 14,
        }}>
          „{toText(item.saját_szavaival)}"
        </div>
      )}
      {lateFields.map(([label, val]) => val && (
        <SubField key={label} label={label} value={val} accent={accent} />
      ))}
    </article>
  );
}

function SubField({ label, value, accent = "purple" }) {
  const a = V2_ACCENT[accent] || V2_ACCENT.purple;
  // Red/green accent cards (Fájdalom/Vágy) get 14px body, default gets 15px.
  const valueFontSize = accent === "red" || accent === "green" ? 14 : 15;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        fontSize: 13, fontWeight: 600, color: a.label,
        letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 4,
      }}>{label}</div>
      <div style={{ color: V2_INK_SOFT, lineHeight: 1.65, fontSize: valueFontSize }}>
        {toText(value)}
      </div>
    </div>
  );
}

function KozosMinta({ value, accent = "purple" }) {
  const a = V2_ACCENT[accent] || V2_ACCENT.purple;
  return (
    <div style={{
      marginTop: 32,
      padding: "24px 28px",
      borderLeft: `3px solid ${a.stripeBorder}`,
      background: a.stripeBg,
      borderRadius: "0 12px 12px 0",
      lineHeight: 1.65, color: V2_INK_SOFT, fontSize: 15,
    }}>
      <div style={{
        fontSize: 13, fontWeight: 600, color: a.label,
        letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10,
      }}>Közös minta</div>
      {toText(value)}
    </div>
  );
}


/* ----------------------- AVATAR (v2 - 19 section spec) ----------------------- */

export function renderAvatarBlueprint(bp) {
  if (!bp) return null;

  // V2 branch: új Ideális Ügyfélprofil (20 szekciós, kétlépcsős).
  if (bp.version === "v2") {
    return renderAvatarBlueprintV2(bp);
  }

  // ---- Field extraction (with legacy fallbacks for backward compat) ----
  const indoklas = F(bp, "indoklas", "miert_legigeretsebb", "reasoning");
  const insights = F(bp, "strategiai_felismeresek", "strategic_insights");
  const alapkep = F(bp, "alapkep", "alapadatok", "basics") || {};
  const jelenlegi = F(bp, "jelenlegi_helyzete", "jelenlegi_helyzet", "current_situation");

  const problema = F(bp, "legnagyobb_problemaja", "biggest_problem");
  // legacy plain string → wrap as object so renderer is uniform
  const problemaObj = typeof problema === "object" && problema !== null
    ? problema
    : (typeof problema === "string" && problema ? { fo_fajdalom: problema } : {});

  const vagyai = F(bp, "vagyai") || (() => {
    const td = F(bp, "valodi_vagy", "true_desire");
    return typeof td === "string" && td ? { kulso_cel: td } : {};
  })();

  const trigger = F(bp, "trigger_pillanat", "trigger_moment") || {};
  const triggerScene = F(trigger, "jelenet", "scene");
  const triggerLine = F(trigger, "elso_szemelyu_mondat", "idezet", "quote");

  const miertIdealis = F(bp, "miert_idealis_ugyfel", "miert_idealis", "why_ideal");
  const segitesz = F(bp, "te_miben_segitesz") || (() => {
    const h = F(bp, "hogyan_segitek", "how_you_help");
    return typeof h === "string" && h ? { atalakulas: h } : {};
  })();

  const celpiac = F(bp, "szituacio_alapu_celpiac", "szituacio_piac", "situation_market");
  const oneLineDef = F(bp, "egy_mondatos_definicio", "one_line_definition");
  const ugyfelnyelv = F(bp, "ugyfelnyelv", "customer_language");

  const profil = F(bp, "szemelyes_profil", "personal_profile") || {};
  const filmek = F(profil, "filmek", "movies");
  const konyvek = F(profil, "konyvek", "books");
  const weboldalak = F(profil, "weboldalak", "websites");
  const influenszerek = F(profil, "influenszerek", "peldakepek", "influencers");

  const psz = F(bp, "pszichografia", "psychographics") || {};
  const szemely = F(psz, "szemelyiseg_jegyek", "szemelyiseg", "personality");
  const ertekek = F(psz, "ertekek", "values");
  const erdeklodes = F(psz, "erdeklodes", "interests");

  const fajdalom = F(bp, "fajdalomprofil", "pain_profile");
  const fajdalomObj = typeof fajdalom === "object" && fajdalom !== null && !Array.isArray(fajdalom)
    ? fajdalom
    : Array.isArray(fajdalom) ? { top_5_fajdalompont: fajdalom } : {};

  const vagyprofil = F(bp, "vagyprofil", "desire_profile");
  const vagyObj = typeof vagyprofil === "object" && vagyprofil !== null && !Array.isArray(vagyprofil)
    ? vagyprofil
    : Array.isArray(vagyprofil) ? { top_5_vagy: vagyprofil } : {};

  const vasarlas = F(bp, "vasarlasi_viselkedes", "buying_behavior");
  const vasarlasObj = typeof vasarlas === "object" && vasarlas !== null
    ? vasarlas : (typeof vasarlas === "string" && vasarlas ? { korabbi_probalkozasok: vasarlas } : null);

  const td = F(bp, "haromdimenzios_profil", "harom_dimenzio", "three_dimensional") || {};
  const naplo = F(bp, "naplobejegyzes");

  // Új szintézis-szekciók (forward-only — régi blueprintekben üres)
  const mitProbalt = F(bp, "mit_probalt");
  const pontosSzavak = F(bp, "pontos_szavak");

  return (
    <div className="bp-stack bp-body" data-testid="avatar-report-v2">
      {/* SECTION 1 — INDOKLÁS (plain prose) */}
      {indoklas && (
        <div style={{ marginBottom: 28 }} data-testid="avatar-section-indoklas">
          <AvEyebrow>INDOKLÁS</AvEyebrow>
          <div style={{ fontSize: 14, color: "#1a1a28", lineHeight: 1.7 }}>{toText(indoklas)}</div>
        </div>
      )}

      {/* SECTION 2 — STRATÉGIAI FELISMERÉSEK (purple callout, numbered) */}
      {Array.isArray(insights) && insights.length > 0 && (
        <div style={{
          background: "rgba(124,108,242,0.06)",
          border: "1px solid rgba(124,108,242,0.22)",
          borderRadius: 10,
          padding: "22px 26px",
          marginBottom: 28,
        }} data-testid="avatar-section-insights">
          <AvEyebrow>STRATÉGIAI FELISMERÉSEK</AvEyebrow>
          <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
            {insights.map((s, i) => (
              <li key={i} style={{ fontSize: 14, color: "#0a0a12", fontWeight: 500, lineHeight: 1.6, marginBottom: i === insights.length - 1 ? 0 : 14 }}>
                <span style={{ color: "#7c6cf2", fontWeight: 600, marginRight: 8 }}>{i + 1}.</span>
                {toText(s)}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* SECTION 3 — ALAPKÉP (white card) */}
      {(F(alapkep, "nev") || F(alapkep, "kor") || F(alapkep, "szakma")) && (
        <div style={{ marginBottom: 28 }} data-testid="avatar-section-alapkep">
          <AvEyebrow>ALAPKÉP</AvEyebrow>
          <div style={{
            background: "white",
            border: "1px solid rgba(124,108,242,0.20)",
            borderRadius: 10,
            padding: "18px 22px",
          }}>
            {F(alapkep, "nev") && (
              <div style={{ fontSize: 19, fontWeight: 600, color: "#0a0a12", letterSpacing: "-0.01em", lineHeight: 1.3, marginBottom: 8 }}>
                {toText(F(alapkep, "nev"))}
                <span
                  data-testid="avatar-name-disclaimer"
                  style={{
                    fontSize: 11,
                    fontStyle: "italic",
                    color: "#6a6a80",
                    marginLeft: 8,
                    fontWeight: 400,
                    letterSpacing: 0,
                  }}
                >
                  (reprezentatív név)
                </span>
              </div>
            )}
            <div style={{ fontSize: 13, color: "#4a4a5c", lineHeight: 1.6 }}>
              {[
                F(alapkep, "kor", "age"),
                F(alapkep, "nem", "gender"),
                F(alapkep, "helyszin", "location"),
                F(alapkep, "szakma", "profession"),
              ].filter(Boolean).map((v, i, arr) => (
                <span key={i}>
                  {toText(v)}
                  {i < arr.length - 1 && <span style={{ color: "#9a9aae", margin: "0 8px" }}>·</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SECTION 4 — JELENLEGI HELYZETE */}
      {jelenlegi && (
        <div style={{ marginBottom: 28 }} data-testid="avatar-section-jelenlegi">
          <AvEyebrow>JELENLEGI HELYZETE</AvEyebrow>
          <div style={{ fontSize: 14, color: "#1a1a28", lineHeight: 1.7 }}>{toText(jelenlegi)}</div>
        </div>
      )}

      {/* SECTION 5 — LEGNAGYOBB PROBLÉMÁJA (red callout) */}
      {(F(problemaObj, "fo_fajdalom") || F(problemaObj, "valodi_elakadas") || F(problemaObj, "miert_frusztralo")) && (
        <div style={{
          background: "rgba(214,128,128,0.05)",
          border: "1px solid rgba(214,128,128,0.16)",
          borderRadius: 10,
          padding: "18px 22px",
          marginBottom: 28,
        }} data-testid="avatar-section-problema">
          <AvEyebrow>LEGNAGYOBB PROBLÉMÁJA</AvEyebrow>
          <AvSubBlock subColor="#b85a5a" label="Fő fájdalom" body={F(problemaObj, "fo_fajdalom")} />
          <AvSubBlock subColor="#b85a5a" label="Valódi elakadás" body={F(problemaObj, "valodi_elakadas")} />
          <AvSubBlock subColor="#b85a5a" label="Miért frusztráló" body={F(problemaObj, "miert_frusztralo")} last />
        </div>
      )}

      {/* SECTION 6 — MIRE VÁGYIK VALÓJÁBAN (green callout) */}
      {(F(vagyai, "kulso_cel") || F(vagyai, "belso_vagy") || F(vagyai, "siker_jelentese")) && (
        <div style={{
          background: "rgba(76,181,133,0.06)",
          border: "1px solid rgba(76,181,133,0.22)",
          borderRadius: 10,
          padding: "18px 22px",
          marginBottom: 28,
        }} data-testid="avatar-section-vagyai">
          <AvEyebrow>MIRE VÁGYIK VALÓJÁBAN</AvEyebrow>
          <AvSubBlock subColor="#2a8a5a" label="Külső cél" body={F(vagyai, "kulso_cel")} />
          <AvSubBlock subColor="#2a8a5a" label="Belső vágy" body={F(vagyai, "belso_vagy")} />
          <AvSubBlock subColor="#2a8a5a" label="Mit jelent neki a siker" body={F(vagyai, "siker_jelentese")} last />
        </div>
      )}

      {/* SECTION 7 — TRIGGER-PILLANAT (heavy purple callout) */}
      {(triggerScene || triggerLine) && (
        <div style={{
          background: "rgba(124,108,242,0.08)",
          border: "1px solid rgba(124,108,242,0.25)",
          borderRadius: 10,
          padding: "22px 26px",
          marginBottom: 28,
        }} data-testid="avatar-section-trigger">
          <AvEyebrow>TRIGGER-PILLANAT</AvEyebrow>
          {triggerScene && (
            <div style={{ fontSize: 14, color: "#1a1a28", lineHeight: 1.7, marginBottom: triggerLine ? 16 : 0 }}>
              {toText(triggerScene)}
            </div>
          )}
          {triggerLine && (
            <div style={{
              fontSize: 17,
              fontStyle: "italic",
              color: "#0a0a12",
              fontWeight: 500,
              lineHeight: 1.5,
            }}>
              {`»${toText(triggerLine).replace(/^[«»"]+|[«»"]+$/g, "")}«`}
            </div>
          )}
        </div>
      )}

      {/* SECTION 8 — MIÉRT IDEÁLIS ÜGYFÉL */}
      {miertIdealis && (
        <div style={{ marginBottom: 28 }} data-testid="avatar-section-idealis">
          <AvEyebrow>MIÉRT IDEÁLIS ÜGYFÉL</AvEyebrow>
          <div style={{ fontSize: 14, color: "#1a1a28", lineHeight: 1.7 }}>{toText(miertIdealis)}</div>
        </div>
      )}

      {/* SECTION 9 — TE MIBEN SEGÍTESZ NEKI (sub-blocks) */}
      {(F(segitesz, "atalakulas") || F(segitesz, "tamogatas") || F(segitesz, "miert_mas_az_eredmeny")) && (
        <div style={{ marginBottom: 28 }} data-testid="avatar-section-segitesz">
          <AvEyebrow>TE MIBEN SEGÍTESZ NEKI</AvEyebrow>
          <AvSubBlock subColor="#7c6cf2" label="Átalakulás" body={F(segitesz, "atalakulas")} />
          <AvSubBlock subColor="#7c6cf2" label="Támogatás" body={F(segitesz, "tamogatas")} />
          <AvSubBlock subColor="#7c6cf2" label="Miért más az eredmény" body={F(segitesz, "miert_mas_az_eredmeny")} last />
        </div>
      )}

      {/* SECTION 10 — SZITUÁCIÓ-ALAPÚ CÉLPIAC (purple callout) */}
      {celpiac && (
        <div style={{
          background: "rgba(124,108,242,0.06)",
          border: "1px solid rgba(124,108,242,0.22)",
          borderRadius: 10,
          padding: "18px 22px",
          marginBottom: 28,
        }} data-testid="avatar-section-celpiac">
          <AvEyebrow>SZITUÁCIÓ-ALAPÚ CÉLPIAC</AvEyebrow>
          <div style={{ fontSize: 17, fontWeight: 500, color: "#0a0a12", lineHeight: 1.5 }}>
            {toText(celpiac)}
          </div>
        </div>
      )}

      {/* SECTION 11 — EGY MONDATOS DEFINÍCIÓ */}
      {oneLineDef && (
        <div style={{
          background: "rgba(124,108,242,0.06)",
          border: "1px solid rgba(124,108,242,0.22)",
          borderRadius: 10,
          padding: "18px 22px",
          marginBottom: 28,
        }} data-testid="avatar-section-onedef">
          <AvEyebrow>EGY MONDATOS DEFINÍCIÓ</AvEyebrow>
          <div style={{ fontSize: 17, fontWeight: 500, color: "#0a0a12", lineHeight: 1.5 }}>
            {toText(oneLineDef)}
          </div>
        </div>
      )}

      {/* SECTION 12 — ÜGYFÉLNYELV (italic list) */}
      {Array.isArray(ugyfelnyelv) && ugyfelnyelv.length > 0 && (
        <div style={{ marginBottom: 28 }} data-testid="avatar-section-ugyfelnyelv">
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <div style={{
              fontSize: 14, fontWeight: 600, color: "#7c6cf2",
              letterSpacing: "0.12em", textTransform: "uppercase",
            }}>ÜGYFÉLNYELV</div>
            <div style={{ fontSize: 12, fontStyle: "italic", color: "#9a9aae" }}>— ahogy az avatar mondaná</div>
          </div>
          {ugyfelnyelv.map((s, i) => (
            <div key={i} style={{
              fontSize: 14, fontStyle: "italic", color: "#1a1a28", lineHeight: 1.7, marginBottom: 4,
            }}>
              <span style={{ color: "#7c6cf2", marginRight: 6 }}>·</span>
              {toText(s)}
            </div>
          ))}
        </div>
      )}

      {/* SECTION 13 — SZEMÉLYES PROFIL (2×2 cards) */}
      {(filmek || konyvek || weboldalak || influenszerek) && (
        <div style={{ marginBottom: 28 }} data-testid="avatar-section-profil">
          <AvEyebrow>SZEMÉLYES PROFIL</AvEyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <ProfileCard label="FILMEK" items={filmek} />
            <ProfileCard label="KÖNYVEK" items={konyvek} />
            <ProfileCard label="WEBOLDALAK" items={weboldalak} />
            <ProfileCard label="INFLUENSZEREK" items={influenszerek} />
          </div>
        </div>
      )}

      {/* SECTION 14 — PSZICHOGRÁFIA (3-column pill grid) */}
      {(szemely || ertekek || erdeklodes) && (
        <div style={{ marginBottom: 28 }} data-testid="avatar-section-psz">
          <AvEyebrow>PSZICHOGRÁFIA</AvEyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <PillCol label="SZEMÉLYISÉG" items={szemely} />
            <PillCol label="ÉRTÉKEK" items={ertekek} />
            <PillCol label="ÉRDEKLŐDÉS" items={erdeklodes} />
          </div>
        </div>
      )}

      {/* SECTION 15 — FÁJDALOMPROFIL (red callout) */}
      {(F(fajdalomObj, "titkos_felelem") || F(fajdalomObj, "fo_panasz") ||
        (Array.isArray(F(fajdalomObj, "top_5_fajdalompont")) && F(fajdalomObj, "top_5_fajdalompont").length > 0) ||
        (Array.isArray(F(fajdalomObj, "top_5_fajdalom")) && F(fajdalomObj, "top_5_fajdalom").length > 0) ||
        F(fajdalomObj, "titkos_jovo_felelem")) && (
        <div style={{
          background: "rgba(214,128,128,0.05)",
          border: "1px solid rgba(214,128,128,0.16)",
          borderRadius: 10,
          padding: "18px 22px",
          marginBottom: 28,
        }} data-testid="avatar-section-fajdalom">
          <AvEyebrow>FÁJDALOMPROFIL</AvEyebrow>
          {/* Új strukturált Top 5 fájdalom (forward-only) - ha van, ez jelenik meg ELŐSZÖR */}
          {Array.isArray(F(fajdalomObj, "top_5_fajdalom")) && F(fajdalomObj, "top_5_fajdalom").length > 0 && (
            <AvStructuredList
              subColor="#b85a5a"
              label="Top 5 fájdalom (sürgősség szerint)"
              items={F(fajdalomObj, "top_5_fajdalom")}
              fieldMap={[
                { key: "hetkoznapi_megnyilvanulas", label: "Hogyan néz ki a hétköznapokban" },
                { key: "erzelmi_hatas", label: "Az érzelmi hatás mögötte" },
                { key: "miert_surgos", label: "Miért érzi olyan sürgősnek, hogy fizessen érte" },
              ]}
              testid="avatar-fajdalom-strukturalt"
            />
          )}
          <AvSubBlock subColor="#b85a5a" label="Titkos félelem" body={F(fajdalomObj, "titkos_felelem")} />
          <AvSubBlock subColor="#b85a5a" label="Fő panasz" body={F(fajdalomObj, "fo_panasz")} />
          {Array.isArray(F(fajdalomObj, "top_5_fajdalompont")) && F(fajdalomObj, "top_5_fajdalompont").length > 0 && (
            <AvSubList
              subColor="#b85a5a"
              label="Top 5 fájdalompont (rövid)"
              items={F(fajdalomObj, "top_5_fajdalompont")}
              last={!F(fajdalomObj, "titkos_jovo_felelem")}
            />
          )}
          {/* Új: Titkos jövő-félelem (forward-only) */}
          {F(fajdalomObj, "titkos_jovo_felelem") && (
            <AvSubBlock
              subColor="#b85a5a"
              label="Titkos jövő-félelem (3-5 év múlva, ha semmi nem változik)"
              body={F(fajdalomObj, "titkos_jovo_felelem")}
              last
            />
          )}
        </div>
      )}

      {/* SECTION 16 — VÁGYPROFIL (green callout) */}
      {(F(vagyObj, "titkos_vagy") || F(vagyObj, "vagyott_elethelyzet") ||
        Array.isArray(F(vagyObj, "top_5_vagy")) || Array.isArray(F(vagyObj, "top_5_erzelem")) ||
        Array.isArray(F(vagyObj, "top_5_hit_onazonositas")) ||
        (Array.isArray(F(vagyObj, "top_5_vagy_strukturalt")) && F(vagyObj, "top_5_vagy_strukturalt").length > 0)) && (
        <div style={{
          background: "rgba(76,181,133,0.06)",
          border: "1px solid rgba(76,181,133,0.22)",
          borderRadius: 10,
          padding: "18px 22px",
          marginBottom: 28,
        }} data-testid="avatar-section-vagy">
          <AvEyebrow>VÁGYPROFIL</AvEyebrow>
          {/* Új strukturált Top 5 vágy (forward-only) - ha van, ez jelenik meg a régi flat lista helyett */}
          {Array.isArray(F(vagyObj, "top_5_vagy_strukturalt")) && F(vagyObj, "top_5_vagy_strukturalt").length > 0 && (
            <AvStructuredList
              subColor="#2a8a5a"
              label="Top 5 vágy (erősség szerint)"
              items={F(vagyObj, "top_5_vagy_strukturalt")}
              fieldMap={[
                { key: "valos_siker_hetkoznapokban", label: "Hogyan néz ki a valós siker a hétköznapokban" },
                { key: "melyebb_erzelmi_mozgatorugo", label: "A mélyebb érzelmi mozgatórugó mögötte" },
                { key: "miert_fektetne_penzt_ebbe", label: "Miért fektetne pénzt ebbe" },
              ]}
              testid="avatar-vagy-strukturalt"
            />
          )}
          <AvSubBlock subColor="#2a8a5a" label="Titkos vágy" body={F(vagyObj, "titkos_vagy")} />
          <AvSubBlock subColor="#2a8a5a" label="Vágyott élethelyzet" body={F(vagyObj, "vagyott_elethelyzet")} />
          {/* Régi flat lista CSAK akkor, ha NINCS strukturált verzió (forward-only kompat) */}
          {Array.isArray(F(vagyObj, "top_5_vagy")) &&
           (!Array.isArray(F(vagyObj, "top_5_vagy_strukturalt")) || F(vagyObj, "top_5_vagy_strukturalt").length === 0) && (
            <AvSubList subColor="#2a8a5a" label="Top 5 vágy" items={F(vagyObj, "top_5_vagy")} />
          )}
          {Array.isArray(F(vagyObj, "top_5_erzelem")) && F(vagyObj, "top_5_erzelem").length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{
                fontSize: 12, fontWeight: 600, color: "#2a8a5a",
                letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 6,
              }}>
                Top 5 érzelem
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {F(vagyObj, "top_5_erzelem").map((p, i) => (
                  <span key={i} style={{
                    background: "rgba(76,181,133,0.10)",
                    border: "1px solid rgba(76,181,133,0.22)",
                    color: "#2a8a5a", padding: "5px 11px", borderRadius: 12,
                    fontSize: 12, fontWeight: 500,
                  }}>{toText(p)}</span>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(F(vagyObj, "top_5_hit_onazonositas")) && (
            <AvSubList subColor="#2a8a5a" label="Top 5 hit / önazonosítás" items={F(vagyObj, "top_5_hit_onazonositas")} last />
          )}
        </div>
      )}

      {/* SECTION 17 — VÁSÁRLÁSI VISELKEDÉS (white card with rows) */}
      {vasarlasObj && (
        <div style={{ marginBottom: 28 }} data-testid="avatar-section-vasarlas">
          <AvEyebrow>VÁSÁRLÁSI VISELKEDÉS</AvEyebrow>
          <div style={{
            background: "white",
            border: "1px solid rgba(124,108,242,0.16)",
            borderRadius: 10,
            padding: "18px 22px",
          }}>
            <Row label="Top 3 döntési trigger" value={F(vasarlasObj, "top_3_dontesi_trigger")} />
            <Row label="Vásárlási gyakoriság" value={F(vasarlasObj, "gyakorisag")} />
            <Row label="Korábbi próbálkozások" value={F(vasarlasObj, "korabbi_probalkozasok")} />
            <Row label="Költési szint" value={F(vasarlasObj, "koltesi_szint")} />
            <Row label="Kedvelt csatornák" value={F(vasarlasObj, "kedvelt_csatornak")} last />
          </div>
        </div>
      )}

      {/* SECTION 19 — HÁROMDIMENZIÓS PROFIL (2×3 cards) */}
      {(F(td, "lat") || F(td, "gondolkodik") || F(td, "gondol") || F(td, "hall") ||
        F(td, "erez") || F(td, "mond") || F(td, "csinal")) && (
        <div style={{ marginBottom: 28 }} data-testid="avatar-section-3d">
          <AvEyebrow>HÁROMDIMENZIÓS PROFIL</AvEyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <DimCard label="LÁT" body={F(td, "lat", "sees")} />
            <DimCard label="GONDOLKODIK" body={F(td, "gondolkodik", "gondol", "thinks")} />
            <DimCard label="HALL" body={F(td, "hall", "hears")} />
            <DimCard label="ÉREZ" body={F(td, "erez", "feels")} />
            <DimCard label="MOND" body={F(td, "mond", "says")} />
            <DimCard label="CSINÁL" body={F(td, "csinal", "does")} />
          </div>
        </div>
      )}

      {/* SECTION 19b — MIT PRÓBÁLT, AMI NEM MŰKÖDÖTT (forward-only) */}
      {Array.isArray(mitProbalt) && mitProbalt.length > 0 && (
        <div style={{
          background: "rgba(214,168,90,0.05)",
          border: "1px solid rgba(214,168,90,0.18)",
          borderRadius: 10,
          padding: "18px 22px",
          marginBottom: 28,
        }} data-testid="avatar-section-mit-probalt">
          <AvEyebrow>MIT PRÓBÁLT, AMI NEM MŰKÖDÖTT</AvEyebrow>
          <AvStructuredList
            subColor="#a87a32"
            label=""
            items={mitProbalt}
            fieldMap={[
              { key: "miert_bukott_meg", label: "Miért bukott meg" },
              { key: "milyen_erzessel_hagyott", label: "Milyen érzéssel hagyott" },
            ]}
            testid="avatar-mit-probalt-list"
            last
          />
        </div>
      )}

      {/* SECTION 19c — A PONTOS SZAVAK, AHOGY EZT MEGFOGALMAZZA (forward-only) */}
      {pontosSzavak && typeof pontosSzavak === "object" && (
        F(pontosSzavak, "fajdalom_szavai") || F(pontosSzavak, "vagy_szavai") ||
        F(pontosSzavak, "belso_monolog_fragmentumok") || F(pontosSzavak, "ellenallas_szavai") ||
        F(pontosSzavak, "attores_szavai")
      ) && (
        <div style={{
          background: "rgba(124,108,242,0.05)",
          border: "1px solid rgba(124,108,242,0.18)",
          borderRadius: 10,
          padding: "18px 22px",
          marginBottom: 28,
        }} data-testid="avatar-section-pontos-szavak">
          <AvEyebrow>A PONTOS SZAVAK, AHOGY EZT MEGFOGALMAZZA</AvEyebrow>
          <AvSubList subColor="#5e4cd8" label="A fájdalom szavai" items={F(pontosSzavak, "fajdalom_szavai")} />
          <AvSubList subColor="#5e4cd8" label="A vágy szavai" items={F(pontosSzavak, "vagy_szavai")} />
          <AvSubList subColor="#5e4cd8" label="Belső monológ-fragmentumok" items={F(pontosSzavak, "belso_monolog_fragmentumok")} />
          <AvSubList subColor="#5e4cd8" label="Az ellenállás szavai" items={F(pontosSzavak, "ellenallas_szavai")} />
          <AvSubList subColor="#5e4cd8" label="Az áttörés szavai" items={F(pontosSzavak, "attores_szavai")} last />
        </div>
      )}

      {/* SECTION 20 — EGY NAP AZ AVATAR FEJÉBEN (italic prose) */}
      {naplo && (
        <div style={{ marginBottom: 12 }} data-testid="avatar-section-naplo">
          <AvEyebrow>EGY NAP AZ AVATAR FEJÉBEN</AvEyebrow>
          <div style={{
            fontSize: 19, fontWeight: 600, color: "#0a0a12",
            letterSpacing: "-0.01em", marginBottom: 14,
          }}>
            Naplóbejegyzés
          </div>
          {toText(naplo).split(/\n\n+/).map((para, i) => (
            <p key={i} style={{
              fontSize: 14, color: "#1a1a28", lineHeight: 1.8,
              fontStyle: "italic", marginBottom: "1em",
            }}>
              {para.trim()}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------- Avatar sub-components ----------------------- */

function AvEyebrow({ children }) {
  return (
    <div style={{
      fontSize: 14, fontWeight: 600, color: "#7c6cf2",
      letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 14,
    }}>{children}</div>
  );
}

function AvSubBlock({ subColor, label, body, last }) {
  if (!body) return null;
  return (
    <div style={{ marginBottom: last ? 0 : 12 }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: subColor,
        letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 6,
      }}>{label}</div>
      <div style={{ fontSize: 14, color: "#1a1a28", lineHeight: 1.6 }}>{toText(body)}</div>
    </div>
  );
}

function AvSubList({ subColor, label, items, last }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{ marginBottom: last ? 0 : 12 }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: subColor,
        letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 6,
      }}>{label}</div>
      {items.map((it, i) => (
        <div key={i} style={{ fontSize: 14, color: "#1a1a28", lineHeight: 1.65, marginBottom: 2 }}>
          <span style={{ color: subColor, marginRight: 6 }}>·</span>
          {toText(it)}
        </div>
      ))}
    </div>
  );
}

// Renders a numbered, multi-field structured list (e.g. Top 5 fájdalom with
// 3 sub-bullets per item). `fieldMap` is an array of {key, label} pairs that
// drives how each item's keys are rendered as sub-bullets.
function AvStructuredList({ subColor, label, items, fieldMap, testid, last }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{ marginBottom: last ? 0 : 16 }} data-testid={testid}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: subColor,
        letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 10,
      }}>{label}</div>
      {items.map((it, i) => {
        const nev = (it && typeof it === "object") ? (it.nev || it.name) : null;
        return (
          <div key={i} style={{ marginBottom: i === items.length - 1 ? 0 : 12 }}>
            <div style={{
              fontSize: 13.5, fontWeight: 600, color: "#1a1a28", marginBottom: 4,
              lineHeight: 1.45,
            }}>
              <span style={{ color: subColor, marginRight: 6 }}>{i + 1}.</span>
              {toText(nev) || `Tétel ${i + 1}`}
            </div>
            <ul style={{
              margin: 0, paddingLeft: 22,
              fontSize: 13.5, color: "#1a1a28", lineHeight: 1.55,
            }}>
              {fieldMap.map((fm) => {
                const val = it && typeof it === "object" ? it[fm.key] : null;
                if (!val) return null;
                return (
                  <li key={fm.key} style={{ marginBottom: 4 }}>
                    <span style={{ color: subColor, fontWeight: 500 }}>{fm.label}: </span>
                    <span style={{ color: "#1a1a28" }}>{toText(val)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function ProfileCard({ label, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{
      background: "rgba(124,108,242,0.04)",
      border: "1px solid rgba(124,108,242,0.14)",
      borderRadius: 8,
      padding: "14px 16px",
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: "#5e4cd8",
        letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8,
      }}>{label}</div>
      {items.map((it, i) => (
        <div key={i} style={{ fontSize: 13, color: "#1a1a28", lineHeight: 1.65 }}>
          <span style={{ color: "#7c6cf2", marginRight: 6 }}>·</span>
          {toText(it)}
        </div>
      ))}
    </div>
  );
}

function PillCol({ label, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div>
      <div style={{
        fontSize: 12, fontWeight: 600, color: "#5e4cd8",
        letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8,
      }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {items.map((p, i) => (
          <span key={i} style={{
            background: "rgba(124,108,242,0.10)",
            border: "1px solid rgba(124,108,242,0.22)",
            color: "#5e4cd8", padding: "5px 11px", borderRadius: 12,
            fontSize: 12, fontWeight: 500,
          }}>{toText(p)}</span>
        ))}
      </div>
    </div>
  );
}

function DimCard({ label, body }) {
  if (!body) return null;
  return (
    <div style={{
      background: "rgba(124,108,242,0.04)",
      border: "1px solid rgba(124,108,242,0.14)",
      borderRadius: 8,
      padding: "14px 16px",
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: "#5e4cd8",
        letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8,
      }}>{label}</div>
      <div style={{ fontSize: 13, color: "#1a1a28", lineHeight: 1.6 }}>{toText(body)}</div>
    </div>
  );
}

function Row({ label, value, last }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  const display = Array.isArray(value)
    ? value.map(toText).filter(Boolean).join("  ·  ")
    : toText(value);
  return (
    <div style={{
      display: "flex",
      gap: 16,
      padding: "10px 0",
      borderBottom: last ? "none" : "1px solid rgba(124,108,242,0.10)",
    }}>
      <div style={{
        flex: "0 0 180px",
        fontSize: 12, fontWeight: 600, color: "#5e4cd8",
        letterSpacing: "0.10em", textTransform: "uppercase", lineHeight: 1.5,
      }}>{label}</div>
      <div style={{ flex: 1, fontSize: 14, color: "#1a1a28", lineHeight: 1.6 }}>{display}</div>
    </div>
  );
}

/* ====================================================================
   NEW OFFER V3 — 19-section Ajánlat blueprint renderer.
   Activates on bp.version === "v3".
   The output is written from the ideal client's INSIDE perspective
   (E/2 narrative), with Netflix-style names, type-adapted language, and
   a collapsed-by-default Forrás expansion section at the bottom.
   ==================================================================== */

function renderOfferBlueprintV3(bp) {
  if (!bp) return null;

  // ─── V3 SECTION READS ───────────────────────────────────────────────
  const magnes = bp.magnesuzenet || {};
  const magnesFo = magnes.fo;

  const pos = bp.ajanlatnev_es_pozicionalas || {};
  const pd = bp.programadatok || {};

  const top5 = bp.top_5_vagyott_eredmeny || {};
  const top5Elemek = Array.isArray(top5.elemek) ? top5.elemek : [];
  const top5Bev = top5.bevezeto || "A program végére:";

  const fajdalom = (bp.fajdalom && Array.isArray(bp.fajdalom.elemek))
    ? bp.fajdalom.elemek : [];
  const gyozelem = (bp.gyozelem && Array.isArray(bp.gyozelem.elemek))
    ? bp.gyozelem.elemek : [];

  const mod = bp.modszer || {};
  const utvonal = (bp.utvonal && Array.isArray(bp.utvonal.szakaszok)) ? bp.utvonal.szakaszok : [];
  const modulok = Array.isArray(bp.modulok) ? bp.modulok : [];
  const amitKapsz = Array.isArray(bp.amit_kapsz_pontosan) ? bp.amit_kapsz_pontosan : [];
  const te = bp.tamogatas_es_elszamoltathatosag || {};
  const teTam = Array.isArray(te.tamogatas) ? te.tamogatas : [];
  const teEls = Array.isArray(te.elszamoltathatosag) ? te.elszamoltathatosag : [];
  const ar = bp.ar || {};
  const bonuszok = Array.isArray(bp.bonuszok) ? bp.bonuszok : [];
  const garancia = bp.garancia || {};
  const gyik = Array.isArray(bp.gyakori_kerdesek) ? bp.gyakori_kerdesek : [];
  const pillerek = Array.isArray(bp.uzeneti_pillerek) ? bp.uzeneti_pillerek : [];
  const sum = bp.alaposszefoglalo || {};
  const forrasObj = bp.forras || {};

  return (
    <div style={{
      background: "#f7f3eb", padding: "48px 52px",
      borderRadius: 14, color: V2_INK,
    }} data-testid="offer-v3-blueprint">

      {/* 01 — MÁGNESÜZENET (CSAK egy fő, nincs alternatíva) */}
      {magnesFo && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-magnesuzenet">
          <V2SectionHeader num="01" label="MÁGNESÜZENET" title="Fő mágnesüzenet" />
          <div style={{
            background: "linear-gradient(135deg, rgba(94,80,247,0.12), rgba(94,80,247,0.04))",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14,
            padding: "30px 34px",
            fontSize: 18, fontWeight: 600, color: V2_INK, lineHeight: 1.6,
            boxShadow: "0 0 24px rgba(94,80,247,0.06)",
          }}>
            {toText(magnesFo)}
          </div>
        </section>
      )}

      {/* 02 — AJÁNLATNÉV ÉS POZICIONÁLÁS */}
      {(pos.ajanlat_neve || pos.pozicionalo_mondat) && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-pozicionalas">
          <V2SectionHeader num="02" label="AJÁNLATNÉV ÉS POZICIONÁLÁS" title="Mi ez és miben más" />
          <div style={{
            background: "rgba(255,255,255,0.65)",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14, padding: "28px 32px",
          }}>
            {pos.ajanlat_neve && (
              <div style={{
                fontSize: 26, fontWeight: 700, color: V2_PURPLE_DEEP,
                marginBottom: 6, lineHeight: 1.2,
              }}>{toText(pos.ajanlat_neve)}</div>
            )}
            {pos.ajanlat_tipusa && (
              <div style={{
                fontSize: 13, fontWeight: 600, color: V2_PURPLE,
                letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 18,
              }}>{toText(pos.ajanlat_tipusa)}</div>
            )}
            {pos.pozicionalo_mondat && (
              <OfferSubField label="Pozicionáló mondat" value={pos.pozicionalo_mondat} />
            )}
            {pos.rovid_alcim && (
              <OfferSubField label="Rövid alcím" value={pos.rovid_alcim} />
            )}
          </div>
        </section>
      )}

      {/* 03 — PROGRAMADATOK */}
      {Object.keys(pd).length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-programadatok">
          <V2SectionHeader num="03" label="PROGRAMADATOK" title="Kinek, miből, hová, mennyi idő alatt" />
          <div style={{
            background: "rgba(255,255,255,0.6)",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14, padding: "28px 32px",
          }}>
            {[
              ["Kinek szól pontosan", pd.kinek_szol],
              ["Milyen helyzetből indul", pd.milyen_helyzetbol],
              ["Hova jut el", pd.hova_jut_el],
              ["Mennyi idő alatt", pd.mennyi_ido_alatt],
              ["Milyen formátumban", pd.milyen_formatumban],
              ["Ajánlattípus", pd.ajanlat_tipus],
              ["Fő mechanizmus", pd.fo_mechanizmus],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} style={{
                display: "grid", gridTemplateColumns: "200px 1fr",
                gap: 16, padding: "12px 0",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                fontSize: 15, lineHeight: 1.75,
              }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                }}>{k}</div>
                <div style={{ color: V2_INK_SOFT }}>{toText(v)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 04 — TOP 5 VÁGYOTT EREDMÉNY (V2-stílusú egyszerű kártya) */}
      {top5Elemek.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-top5">
          <V2SectionHeader num="04" label="TOP 5 EREDMÉNY" title="Mit érsz el a program végére" />
          <div style={{
            background: "rgba(255,255,255,0.6)",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14, padding: "28px 32px",
          }}>
            {top5Bev && (
              <div style={{
                fontSize: 15, fontWeight: 600, color: V2_INK,
                marginBottom: 16, lineHeight: 1.5,
              }}>{toText(top5Bev)}</div>
            )}
            <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
              {top5Elemek.map((el, i) => (
                <li key={i} style={{
                  display: "flex", gap: 14, alignItems: "flex-start",
                  padding: "10px 0",
                  borderBottom: i < top5Elemek.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none",
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 999,
                    background: V2_PURPLE_BG_SOFT, color: V2_PURPLE_DEEP,
                    fontSize: 12, fontWeight: 700,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>{i + 1}</div>
                  <div style={{ flex: 1, color: V2_INK_SOFT, lineHeight: 1.65, fontSize: 15 }}>
                    {toText(el)}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* 05 — FÁJDALOM (Top 3 probléma) */}
      {fajdalom.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-fajdalom">
          <V2SectionHeader num="05" label="FÁJDALOM" title="Top 3 probléma — az ügyfél saját hangján" accent="red" />
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {fajdalom.map((it, i) => (
              <OfferPainCard key={i} idx={i} item={it} />
            ))}
          </div>
        </section>
      )}

      {/* 06 — GYŐZELEM (Top 3 vágy) */}
      {gyozelem.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-gyozelem">
          <V2SectionHeader num="06" label="GYŐZELEM" title="Top 3 vágyott állapot — az ügyfél hangján" accent="green" />
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {gyozelem.map((it, i) => (
              <OfferVictoryCard key={i} idx={i} item={it} />
            ))}
          </div>
        </section>
      )}

      {/* 07 — MÓDSZER */}
      {Object.keys(mod).length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-modszer">
          <V2SectionHeader num="07" label="MÓDSZER" title="Saját módszer / mechanizmus" />
          <div style={{
            background: "rgba(255,255,255,0.6)",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14, padding: "32px 36px",
          }}>
            {mod.neve && (
              <div style={{
                fontSize: 22, fontWeight: 700, color: V2_PURPLE_DEEP,
                marginBottom: 18, lineHeight: 1.3,
              }}>{toText(mod.neve)}</div>
            )}
            {mod.mi_ez && <OfferSubField label="Mi ez" value={mod.mi_ez} />}
            {mod.miert_mukodik && <OfferSubField label="Miért működik" value={mod.miert_mukodik} />}
            {mod.miben_mas && <OfferSubField label="Miben más" value={mod.miben_mas} />}
            {Array.isArray(mod.fo_elemei) && mod.fo_elemei.length > 0 && (
              <V2List label="Fő elemei" items={mod.fo_elemei} />
            )}
          </div>
        </section>
      )}

      {/* 08 — FEJLŐDÉSI ÍV (Sprint 3.13: cím ajánlattípus szerint override-olható) */}
      {utvonal.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-utvonal">
          <V2SectionHeader
            num="08"
            label={bp?.offer_type_meta?.section_08?.label || "FEJLŐDÉSI ÍV"}
            title={bp?.offer_type_meta?.section_08?.title || "A résztvevő útja"}
          />
          <OfferRoadmap szakaszok={utvonal} />
        </section>
      )}

      {/* 09 — Cím ajánlattípus szerint: tagsági → HOGYAN MŰKÖDIK, kurzus → MODULOK, coaching → LÉPÉSEK, workshop → WORKSHOP BLOKKOK, szolgáltatás → EGYÜTTMŰKÖDÉSI FOLYAMAT, egyéb → A PROGRAM ELEMEI */}
      {modulok.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-modulok">
          <V2SectionHeader
            num="09"
            label={bp?.offer_type_meta?.section_09?.label || "A PROGRAM ELEMEI"}
            title={bp?.offer_type_meta?.section_09?.title || "A program felépítése"}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {modulok.map((m, i) => (
              <OfferModuleCard key={i} idx={i} mod={m} numberPrefix={bp?.offer_type_meta?.section_09?.prefix} />
            ))}
          </div>
        </section>
      )}

      {/* 10 — AMIT KAPSZ PONTOSAN (kurált rövid lista, NEM auto-felsorolás) */}
      {amitKapsz.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-amit-kapsz">
          <V2SectionHeader num="10" label="AMIT KAPSZ PONTOSAN" title="A program konkrét elemei" />
          <div style={{
            background: "rgba(255,255,255,0.6)",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14, padding: "28px 32px",
          }}>
            {amitKapsz.map((it, i) => (
              <div key={i} style={{
                padding: "14px 0",
                borderBottom: i < amitKapsz.length - 1 ? "1px solid rgba(0,0,0,0.06)" : "none",
              }}>
                <div style={{
                  fontSize: 16, fontWeight: 600, color: V2_INK,
                  marginBottom: 4, lineHeight: 1.35,
                }}>{toText(it.nev)}</div>
                {it.leiras && (
                  <div style={{ fontSize: 14, color: V2_INK_SOFT, lineHeight: 1.65 }}>
                    {toText(it.leiras)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 11 — TÁMOGATÁS ÉS ELSZÁMOLTATHATÓSÁG (tagolt, vastag kiemelésekkel) */}
      {(teTam.length > 0 || teEls.length > 0) && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-tamogatas">
          <V2SectionHeader num="11" label="TÁMOGATÁS" title="Támogatás és elszámoltathatóság" />
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 24,
          }}>
            {teTam.length > 0 && (
              <div style={{
                background: "rgba(255,255,255,0.6)",
                border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
                borderRadius: 14, padding: "24px 28px",
              }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                  letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 14,
                }}>Támogatás</div>
                {teTam.map((t, i) => (
                  <div key={i} style={{ marginBottom: 14, fontSize: 14.5, lineHeight: 1.65 }}>
                    <strong style={{ color: V2_INK, fontWeight: 700 }}>{toText(t.cim)}: </strong>
                    <span style={{ color: V2_INK_SOFT }}>{toText(t.leiras)}</span>
                  </div>
                ))}
              </div>
            )}
            {teEls.length > 0 && (
              <div style={{
                background: "rgba(255,255,255,0.6)",
                border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
                borderRadius: 14, padding: "24px 28px",
              }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                  letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 14,
                }}>Elszámoltathatóság</div>
                {teEls.map((t, i) => (
                  <div key={i} style={{ marginBottom: 14, fontSize: 14.5, lineHeight: 1.65 }}>
                    <strong style={{ color: V2_INK, fontWeight: 700 }}>{toText(t.cim)}: </strong>
                    <span style={{ color: V2_INK_SOFT }}>{toText(t.leiras)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* 12 — ÁR (emberibb, korábbi verzió-szerű — bullet tartalom + emberi magyarázat) */}
      {Object.keys(ar).length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-ar">
          <V2SectionHeader num="12" label="ÁR" title="Az ajánlat" />
          <div style={{
            background: "rgba(255,255,255,0.65)",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14, padding: "30px 34px",
          }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 14, marginBottom: 6 }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                letterSpacing: "0.10em", textTransform: "uppercase",
                width: "100%",
              }}>Program ára</div>
              {ar.ar && (
                <div style={{
                  fontSize: 24, fontWeight: 700, color: V2_INK, lineHeight: 1.25,
                }}>{toText(ar.ar)}</div>
              )}
              {ar.reszletfizetes && (
                <div style={{ fontSize: 15, color: V2_MUTED, lineHeight: 1.5 }}>
                  vagy {toText(ar.reszletfizetes)}
                </div>
              )}
            </div>

            {(ar.fizetesi_mod || ar.hozzaferes) && (
              <div style={{ marginTop: 18, marginBottom: 4 }}>
                {ar.fizetesi_mod && <OfferSubField label="Fizetési mód" value={ar.fizetesi_mod} />}
                {ar.hozzaferes && <OfferSubField label="Hozzáférés" value={ar.hozzaferes} />}
              </div>
            )}

            {Array.isArray(ar.tartalmazza) && ar.tartalmazza.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
                  letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 12,
                }}>Tartalmazza</div>
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
                  {ar.tartalmazza.map((b, i) => (
                    <li key={i} style={{
                      position: "relative", paddingLeft: 24,
                      marginBottom: 8, fontSize: 15, lineHeight: 1.7, color: V2_INK_SOFT,
                    }}>
                      <span style={{
                        position: "absolute", left: 0, top: 1,
                        color: V2_PURPLE, fontWeight: 700, fontSize: 15,
                      }}>✓</span>
                      {toText(b)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {ar.emberi_magyarazat && (
              <div style={{
                marginTop: 22, padding: "16px 18px",
                background: "rgba(94,80,247,0.06)",
                borderLeft: `3px solid ${V2_PURPLE}`,
                borderRadius: "0 8px 8px 0",
                fontSize: 14.5, lineHeight: 1.7, color: V2_INK_SOFT,
                fontStyle: "italic",
              }}>
                {toText(ar.emberi_magyarazat)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* 13 — BÓNUSZOK */}
      {bonuszok.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-bonuszok">
          <V2SectionHeader num="13" label="BÓNUSZOK" title="Bónuszok, amik konkrét akadályt oldanak" />
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {bonuszok.map((b, i) => (
              <div key={i} style={{
                background: "rgba(255,255,255,0.6)",
                border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
                borderRadius: 14, padding: "22px 26px",
              }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 14, marginBottom: 12,
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 999,
                    background: V2_PURPLE_BG_SOFT, color: V2_PURPLE_DEEP,
                    fontSize: 13, fontWeight: 700,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>B{i + 1}</div>
                  <div style={{ fontSize: 17, fontWeight: 600, color: V2_INK }}>
                    {toText(b.nev)}
                  </div>
                </div>
                {b.mit_ad && <OfferSubField label="Mit ad" value={b.mit_ad} />}
                {b.milyen_akadalyt_old && <OfferSubField label="Milyen akadályt old" value={b.milyen_akadalyt_old} />}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 14 — GARANCIA */}
      {Object.keys(garancia).length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-garancia">
          <V2SectionHeader num="14" label="GARANCIA" title="Garancia és kockázatcsökkentés" />
          <div style={{
            background: "rgba(255,255,255,0.6)",
            border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 14, padding: "28px 32px",
          }}>
            {garancia.neve && (
              <div style={{
                fontSize: 20, fontWeight: 700, color: V2_PURPLE_DEEP,
                marginBottom: 14, lineHeight: 1.3,
              }}>{toText(garancia.neve)}</div>
            )}
            {garancia.mit_vallal && <OfferSubField label="Mit vállal" value={garancia.mit_vallal} />}
            {garancia.mit_kell_tennie_a_resztvevonek && <OfferSubField label="Mit kell tennie a résztvevőnek" value={garancia.mit_kell_tennie_a_resztvevonek} />}
            {garancia.meddig_ervenyes && <OfferSubField label="Meddig érvényes" value={garancia.meddig_ervenyes} />}
          </div>
        </section>
      )}

      {/* 15 — GYAKORI KÉRDÉSEK (kérdés cím 18px) */}
      {gyik.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-gyik">
          <V2SectionHeader num="15" label="GYAKORI KÉRDÉSEK" title="Empatikus válaszok a leggyakoribb kérdésekre" />
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {gyik.map((k, i) => (
              <V2Collapsible
                key={i}
                num={`15.${i + 1}`}
                label="KÉRDÉS"
                title={toText(k.kerdes)}
                titleStyle={{ fontSize: 18 }}
              >
                {k.mi_van_mogotte && <OfferSubField label="Mi van mögötte" value={k.mi_van_mogotte} />}
                {k.empatikus_valasz && <OfferSubField label="Empatikus válasz" value={k.empatikus_valasz} />}
              </V2Collapsible>
            ))}
          </div>
        </section>
      )}

      {/* 16 — ÜZENETI PILLÉREK */}
      {pillerek.length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-pillerek">
          <V2SectionHeader num="16" label="ÜZENETI PILLÉREK" title="Ajánlati üzenetek alapjai" />
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {pillerek.map((p, i) => (
              <div key={i} style={{
                background: "rgba(255,255,255,0.6)",
                border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
                borderRadius: 14, padding: "22px 26px",
              }}>
                <div style={{
                  fontSize: 17, fontWeight: 600, color: V2_INK,
                  marginBottom: 10, lineHeight: 1.3,
                }}>{toText(p.nev)}</div>
                {p.alapgondolat && <OfferSubField label="Alapgondolat" value={p.alapgondolat} />}
                {p.mire_reagal_az_avatarban && <OfferSubField label="Mire reagál az Avatarban" value={p.mire_reagal_az_avatarban} />}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 17 — ALAPÖSSZEFOGLALÓ (kötelező szekció, kiemelt lila keretes blokk) */}
      {Object.keys(sum).length > 0 && (
        <section style={{ marginBottom: 56 }} data-testid="offer-v3-sec-osszefoglalo">
          <V2SectionHeader num="17" label="ALAPÖSSZEFOGLALÓ" title="Injektálható összefoglaló a tartalomgeneráló ügynököknek" />
          <div style={{
            background: "linear-gradient(135deg, rgba(94,80,247,0.12), rgba(94,80,247,0.04))",
            border: `1.5px solid ${V2_PURPLE_BORDER_SOFT}`,
            borderRadius: 16, padding: "32px 36px",
            boxShadow: "0 0 28px rgba(94,80,247,0.08)",
          }}>
            {sum.ajanlat_roviden && (
              <p style={{ margin: "0 0 22px", lineHeight: 1.75, color: V2_INK_SOFT, fontSize: 15 }}>
                {toText(sum.ajanlat_roviden)}
              </p>
            )}
            {[
              ["Ajánlat neve", sum.ajanlat_neve],
              ["Ajánlattípus", sum.ajanlat_tipusa],
              ["Célközönség", sum.celkozonseg],
              ["Fő probléma", sum.fo_problema],
              ["Fő vágyott eredmény", sum.fo_vagyott_eredmeny],
              ["Fő mágnesüzenet", sum.fo_magnesuzenet],
              ["Programígéret", sum.programigeret],
              ["Fő mechanizmus", sum.fo_mechanizmus],
              ["Ár / formátum", sum.ar_formatum],
              ["Garancia", sum.garancia],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} style={{ marginBottom: 10, fontSize: 15, lineHeight: 1.65 }}>
                <strong style={{ color: V2_INK }}>{k}: </strong>
                <span style={{ color: V2_INK_SOFT }}>{toText(v)}</span>
              </div>
            ))}
            <V2List label="Programútvonal" items={sum.programutvonal} />
            <V2List label="Top 3 probléma" items={sum.top_3_problema} />
            <V2List label="Top 3 győzelem" items={sum.top_3_gyozelem} />
            <V2List label="Top 5 vágyott eredmény" items={sum.top_5_vagyott_eredmeny} />
            <V2List label="Mit kap pontosan" items={sum.mit_kap_pontosan} />
            <V2List label="Támogatás és elszámoltathatóság" items={sum.tamogatas_elszamoltathatosag} />
            <V2List label="Gyakori kérdések fő témái" items={sum.gyakori_kerdesek_temai} />
            <V2List label="Üzeneti pillérek" items={sum.uzeneti_pillerek} />
          </div>
        </section>
      )}

      {/* 18 — FORRÁS (alapból csukva) */}
      <OfferSourceExpansionSection bp={bp} forrasObj={forrasObj} />
    </div>
  );
}

function OfferPainCard({ idx, item }) {
  const a = V2_ACCENT.red;
  const Icon = PAIN_ICONS[idx % PAIN_ICONS.length];
  // Backwards-compat: older blueprints may still use `nev` as the only title.
  const cimRovid = toText(item.cim_rovid || item.nev);
  const belso = item.belso_mondat;
  const fields = [
    ["Hogyan jelenik meg", item.hogyan_jelenik_meg],
    ["Miért fáj", item.miert_faj],
    ["Hogyan reagál erre az ajánlat", item.hogyan_reagal_az_ajanlat],
  ];
  return (
    <article style={{
      background: "rgba(255,255,255,0.78)",
      border: `1px solid ${a.cardBorder}`,
      borderRadius: 16, padding: "34px 38px",
      boxShadow: "0 1px 12px rgba(0,0,0,0.04)",
    }}>
      {/* Emotional opener: icon · short title · quoted inner monologue */}
      <header style={{
        display: "flex", alignItems: "flex-start", gap: 22, marginBottom: 22,
      }}>
        <div style={{
          width: 62, height: 62, borderRadius: 999,
          background: a.badgeBg, color: a.eyebrow,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }} aria-hidden="true">
          {Icon && <Icon size={28} strokeWidth={1.6} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: a.eyebrow,
            letterSpacing: "0.14em", textTransform: "uppercase",
            marginBottom: 6,
          }}>{`0${idx + 1}`} · PROBLÉMA</div>
          <div style={{
            fontSize: 24, fontWeight: 700, color: V2_INK,
            lineHeight: 1.2, letterSpacing: "-0.01em",
            marginBottom: belso ? 8 : 0,
          }}>{cimRovid}</div>
          {belso && (
            <div style={{
              fontSize: 16, fontWeight: 500, color: a.eyebrow,
              fontStyle: "italic", lineHeight: 1.55,
            }}>„{toText(belso)}"</div>
          )}
        </div>
      </header>
      <hr style={{
        border: "none", height: 1,
        background: a.cardBorder,
        margin: "0 0 22px",
      }} />
      {fields.map(([label, val]) => val && (
        <SubField key={label} label={label} value={val} accent="red" />
      ))}
    </article>
  );
}

function OfferVictoryCard({ idx, item }) {
  const a = V2_ACCENT.green;
  const Icon = VICTORY_ICONS[idx % VICTORY_ICONS.length];
  const cimRovid = toText(item.cim_rovid || item.nev);
  const gMondat = item.gyozelem_mondat;
  const fields = [
    ["Mit érsz el", item.mit_ersz_el],
    ["Hogyan jelenik meg a való életben", item.hogyan_jelenik_meg],
    ["Miért fontos neked", item.miert_fontos_neked],
  ];
  return (
    <article style={{
      background: "rgba(255,255,255,0.78)",
      border: `1px solid ${a.cardBorder}`,
      borderRadius: 16, padding: "34px 38px",
      boxShadow: "0 1px 12px rgba(0,0,0,0.04)",
    }}>
      <header style={{
        display: "flex", alignItems: "flex-start", gap: 22, marginBottom: 22,
      }}>
        <div style={{
          width: 62, height: 62, borderRadius: 999,
          background: a.badgeBg, color: a.eyebrow,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }} aria-hidden="true">
          {Icon && <Icon size={28} strokeWidth={1.6} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: a.eyebrow,
            letterSpacing: "0.14em", textTransform: "uppercase",
            marginBottom: 6,
          }}>{`0${idx + 1}`} · GYŐZELEM</div>
          <div style={{
            fontSize: 24, fontWeight: 700, color: V2_INK,
            lineHeight: 1.2, letterSpacing: "-0.01em",
            marginBottom: gMondat ? 8 : 0,
          }}>{cimRovid}</div>
          {gMondat && (
            <div style={{
              fontSize: 16, fontWeight: 500, color: a.eyebrow,
              fontStyle: "italic", lineHeight: 1.55,
            }}>„{toText(gMondat)}"</div>
          )}
        </div>
      </header>
      <hr style={{
        border: "none", height: 1,
        background: a.cardBorder,
        margin: "0 0 22px",
      }} />
      {fields.map(([label, val]) => val && (
        <SubField key={label} label={label} value={val} accent="green" />
      ))}
    </article>
  );
}

function OfferSubField({ label, value }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        fontSize: 13, fontWeight: 600, color: V2_PURPLE_DEEP,
        letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 4,
      }}>{label}</div>
      <div style={{ color: V2_INK_SOFT, lineHeight: 1.75, fontSize: 15 }}>
        {toText(value)}
      </div>
    </div>
  );
}

function OfferRoadmap({ szakaszok }) {
  // Polished Netflix-style roadmap. Each module is centered with the same
  // vertical rhythm. Connector arrows live in their own dedicated cells so
  // the icons stay perfectly aligned across the grid.
  const count = szakaszok.length;
  if (count === 0) return null;
  const cols = Math.min(count, 4);
  const rows = Math.ceil(count / cols);
  return (
    <div style={{
      background: "linear-gradient(135deg, rgba(94,80,247,0.07), rgba(94,80,247,0.015))",
      border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
      borderRadius: 20,
      padding: "46px 40px",
    }}>
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 48,
      }}>
        {Array.from({ length: rows }).map((_, rowIdx) => {
          const rowItems = szakaszok.slice(rowIdx * cols, rowIdx * cols + cols);
          const itemsInRow = rowItems.length;
          return (
            <div key={rowIdx} style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              alignItems: "start",
            }}>
              {rowItems.map((sz, colIdx) => {
                const absoluteIdx = rowIdx * cols + colIdx;
                const iconKey = (sz.ikon || "").toString().toLowerCase().trim();
                const Icon = ROADMAP_ICON_MAP[iconKey]
                  || ROADMAP_ICON_FALLBACK[absoluteIdx % ROADMAP_ICON_FALLBACK.length];
                const isLastInRow = colIdx === itemsInRow - 1;
                const isLastOverall = absoluteIdx === count - 1;
                return (
                  <div key={colIdx} style={{
                    position: "relative",
                    display: "flex", flexDirection: "column",
                    alignItems: "center", textAlign: "center",
                    padding: "0 10px",
                  }}>
                    {/* Small step number (eyebrow) */}
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: V2_PURPLE,
                      letterSpacing: "0.18em", textTransform: "uppercase",
                      opacity: 0.65,
                      marginBottom: 10,
                    }}>{String(sz.szam || (absoluteIdx + 1)).padStart(2, "0")}</div>
                    {/* Icon circle (the visual hero) */}
                    <div style={{
                      width: 78, height: 78, borderRadius: 999,
                      background: "#ffffff",
                      border: `1.5px solid ${V2_PURPLE_BORDER_SOFT}`,
                      color: V2_PURPLE_DEEP,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 6px 20px rgba(94,80,247,0.10)",
                      marginBottom: 14,
                    }} aria-hidden="true">
                      {Icon && <Icon size={30} strokeWidth={1.5} />}
                    </div>
                    {/* Module name */}
                    <div style={{
                      fontSize: 14, fontWeight: 600, color: V2_INK,
                      lineHeight: 1.35, maxWidth: 150,
                    }}>
                      {toText(sz.neve)}
                    </div>
                    {/* In-row connector arrow — centered on the icon vertically */}
                    {!isLastInRow && !isLastOverall && (
                      <div style={{
                        position: "absolute",
                        right: -10, top: 21 + 78 / 2 - 11,
                        width: 22, height: 22,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: V2_PURPLE,
                        opacity: 0.5,
                        pointerEvents: "none",
                      }} aria-hidden="true">
                        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 7h14M11 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OfferModuleCard({ idx, mod, numberPrefix }) {
  // Sprint 3.13: számozás típus alapján — tagsági/workshop/szolgáltatás:
  // 01/02/03; kurzus: M1/M2/M3; coaching: L1/L2/L3
  const label = (() => {
    const p = (numberPrefix || "M1").toUpperCase();
    if (p.startsWith("M")) return `M${idx + 1}`;
    if (p.startsWith("L")) return `L${idx + 1}`;
    return String(idx + 1).padStart(2, "0");
  })();
  return (
    <article style={{
      background: "rgba(255,255,255,0.6)",
      border: `1px solid ${V2_PURPLE_BORDER_SOFT}`,
      borderRadius: 14, padding: "26px 30px",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 14, marginBottom: 14,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 999,
          background: V2_PURPLE_BG_SOFT, color: V2_PURPLE_DEEP,
          fontSize: 13, fontWeight: 700,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>{label}</div>
        <div style={{ fontSize: 19, fontWeight: 600, color: V2_INK, lineHeight: 1.3 }}>
          {toText(mod.nev)}
        </div>
      </div>
      {mod.cel && <OfferSubField label="Cél" value={mod.cel} />}
      {mod.leiras && (
        <p style={{ margin: "0 0 14px", color: V2_INK_SOFT, lineHeight: 1.7, fontSize: 14.5 }}>
          {toText(mod.leiras)}
        </p>
      )}
      {Array.isArray(mod.merfoldkovek) && <V2List label="Mérföldkövek" items={mod.merfoldkovek} />}
      {Array.isArray(mod.akciolepesek) && <V2List label="Akciólépések" items={mod.akciolepesek} />}
    </article>
  );
}

function OfferSourceExpansionSection({ bp, forrasObj }) {
  const forras = forrasObj || {};
  const otletek = Array.isArray(forras.otletek) ? forras.otletek : [];
  const bevezeto = forras.bevezeto_bekezdes ||
    "Ez egy erős kiindulópont. Az alábbi ötletek segíthetnek mélyebbé, konkrétabbá és még eladhatóbbá tenni az ajánlatot. Minél több konkrét példát, ügyféltörténetet, eredményt vagy saját módszertani részletet adsz hozzá, annál pontosabb lesz minden későbbi tartalom, amit az app ebből épít.";
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [values, setValues] = React.useState(() => Object.fromEntries(otletek.map((h) => [h, ""])));

  React.useEffect(() => {
    setValues((prev) => {
      const next = {};
      otletek.forEach((h) => { next[h] = prev[h] || ""; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otletek.join("|")]);

  const onExpand = bp?._on_expand_source;

  async function handleSubmit() {
    const trimmed = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, (v || "").trim()]).filter(([, v]) => v),
    );
    if (Object.keys(trimmed).length === 0) {
      // eslint-disable-next-line no-alert
      alert("Tölts ki legalább egy mezőt a bővítéshez.");
      return;
    }
    if (!onExpand) return;
    setSubmitting(true);
    try {
      await onExpand(trimmed);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <V2Collapsible num="18" label="FORRÁS" title="Értékelés és bővítés" defaultOpen={false}>
      <div data-testid="offer-v3-sec-forras">
        <p style={{ margin: "0 0 18px", color: V2_INK_SOFT, lineHeight: 1.75, fontSize: 15 }}>
          {toText(bevezeto)}
        </p>
        {otletek.length > 0 && (
          <div style={{ marginTop: 6, marginBottom: 22 }}>
            <div style={{
              fontSize: 13, fontWeight: 600, color: V2_PURPLE,
              letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 12,
            }}>Ötletek a mélyítéshez</div>
            <ul style={{ margin: 0, paddingLeft: 18, listStyle: "none" }}>
              {otletek.map((h, i) => (
                <li key={i} style={{
                  position: "relative", paddingLeft: 16,
                  marginBottom: 8, lineHeight: 1.75, color: V2_INK_SOFT, fontSize: 15,
                }}>
                  <span style={{
                    position: "absolute", left: 0, top: 10,
                    width: 6, height: 6, borderRadius: 999, background: V2_PURPLE,
                  }} />
                  {toText(h)}
                </li>
              ))}
            </ul>
          </div>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={otletek.length === 0 || !onExpand}
          style={{
            marginTop: 4,
            background: V2_PURPLE, color: "#ffffff",
            border: "none", borderRadius: 999,
            padding: "12px 22px",
            fontSize: 14.5, fontWeight: 600,
            cursor: otletek.length > 0 && onExpand ? "pointer" : "not-allowed",
            opacity: otletek.length > 0 && onExpand ? 1 : 0.5,
            letterSpacing: "0.01em",
            boxShadow: "0 2px 12px rgba(94,80,247,0.30)",
          }}
          data-testid="offer-v3-expand-source-btn"
        >
          + Forrásanyag bővítése
        </button>

        {open && (
          <SourceExpansionModal
            hianyzo={otletek}
            values={values}
            setValues={setValues}
            onClose={() => setOpen(false)}
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        )}
      </div>
    </V2Collapsible>
  );
}


/* ----------------------- OFFER (v2 — Hormozi Grand Slam 7-section) ----------------------- */

/**
 * 7-section Ajánlat Blueprint renderer. Activates when the blueprint
 * contains the v2 markers (snapshot, modulok, messaging_hooks). Old saved
 * offers fall through to the legacy renderer below.
 */
function renderOfferBlueprintLegacy(bp) {
  const oneLine = F(bp, "egy_mondatos_ajanlat");
  const snap = F(bp, "snapshot") || {};
  const modulok = Array.isArray(F(bp, "modulok")) ? bp.modulok : [];
  const pricing = F(bp, "ar_es_kockazatcsokkentes") || {};
  const bonuszok = Array.isArray(F(bp, "bonuszok")) ? bp.bonuszok : [];
  const gyik = Array.isArray(F(bp, "gyik")) ? bp.gyik : [];
  const hooks = Array.isArray(F(bp, "messaging_hooks")) ? bp.messaging_hooks : [];
  const amitMegkapsz = Array.isArray(F(pricing, "amit_megkapsz"))
    ? pricing.amit_megkapsz
    : [];

  const top3Problema = Array.isArray(F(snap, "top3_problema")) ? snap.top3_problema : [];
  const top3Gyozelem = Array.isArray(F(snap, "top3_gyozelem")) ? snap.top3_gyozelem : [];
  const programNev = F(snap, "program_nev");
  const eredmeny = F(snap, "eredmeny");
  const celkozonseg = F(snap, "celkozonseg");
  const programHossz = F(snap, "program_hossz");
  const programFormatum = F(snap, "program_formatum");

  const garancia = F(pricing, "garancia") || {};

  // Number of rows in the problem→victory table
  const arcRows = Math.max(top3Problema.length, top3Gyozelem.length);

  // End-of-program label for the arc — derive from program_hossz
  const endLabel = (() => {
    const s = (toText(programHossz) || "").trim();
    if (!s) return `${modulok.length}. modul`;
    return s.replace(/^(\d+)\s*(hét|nap|hónap|hét|hetes)/i, (_m, num, unit) => {
      const clean = unit.toLowerCase();
      if (clean.startsWith("hét") || clean === "hetes") return `${num}. hét`;
      if (clean.startsWith("hónap")) return `${num}. hónap`;
      if (clean.startsWith("nap")) return `${num}. nap`;
      return `${num}`;
    });
  })();

  // Two-word line-break for ivneve/nev in the arc
  const arcLabel = (m) => {
    const raw = (toText(F(m, "ivneve")) || toText(F(m, "nev")) || "").trim();
    if (!raw) return "";
    const upper = raw.toUpperCase();
    const words = upper.split(/\s+/);
    if (words.length <= 1) return upper;
    if (words.length === 2) {
      return (
        <>
          {words[0]}
          <br />
          {words[1]}
        </>
      );
    }
    // 3+ words: break after 2nd word
    return (
      <>
        {words.slice(0, 2).join(" ")}
        <br />
        {words.slice(2).join(" ")}
      </>
    );
  };

  return (
    <div className="bp-stack bp-body" data-testid="offer-report-v2">
      {/* SECTION 1 — EGY MONDATBAN (magnet message, bigger padding + larger body) */}
      {oneLine && (
        <div
          style={{
            background: "rgba(124,108,242,0.06)",
            border: "1px solid rgba(124,108,242,0.22)",
            borderRadius: 14,
            padding: "32px 36px",
            marginBottom: 32,
          }}
          data-testid="offer-section-one-line"
        >
          <div
            style={{
              fontSize: 11,
              color: "#7c6cf2",
              letterSpacing: "0.18em",
              fontWeight: 600,
              textTransform: "uppercase",
              marginBottom: 12,
            }}
          >
            EGY MONDATBAN
          </div>
          <div
            style={{
              fontSize: 19,
              color: "#0a0a12",
              fontWeight: 500,
              lineHeight: 1.45,
            }}
          >
            {toText(oneLine)}
          </div>
        </div>
      )}

      {/* SECTION 2 — A PROGRAM SNAPSHOT (white card, 2-col metadata only) */}
      {(programNev || celkozonseg || eredmeny) && (
        <div
          style={{
            background: "white",
            border: "1px solid rgba(124,108,242,0.20)",
            borderRadius: 10,
            padding: "22px 26px",
            marginBottom: 28,
          }}
          data-testid="offer-section-snapshot"
        >
          <OEyebrow>A PROGRAM SNAPSHOT</OEyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", rowGap: 10, columnGap: 16 }}>
            {programNev && (
              <>
                <SnapLabel>Program neve</SnapLabel>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#0a0a12" }}>{toText(programNev)}</div>
              </>
            )}
            {eredmeny && (
              <>
                <SnapLabel>Eredmény</SnapLabel>
                <div style={{ fontSize: 14, color: "#1a1a28" }}>{toText(eredmeny)}</div>
              </>
            )}
            {celkozonseg && (
              <>
                <SnapLabel>Célközönség</SnapLabel>
                <div style={{ fontSize: 14, color: "#1a1a28", lineHeight: 1.55 }}>{toText(celkozonseg)}</div>
              </>
            )}
            {(programHossz || programFormatum) && (
              <>
                <SnapLabel>Hossz · Formátum</SnapLabel>
                <div style={{ fontSize: 14, color: "#1a1a28" }}>
                  {toText(programHossz)}
                  {programHossz && programFormatum ? " · " : ""}
                  {toText(programFormatum)}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* SECTION 3 — TOP 3 PROBLÉMA → GYŐZELEM (own section, horizontally aligned rows) */}
      {arcRows > 0 && (
        <div
          style={{
            background: "white",
            border: "1px solid rgba(124,108,242,0.15)",
            borderRadius: 12,
            padding: "28px 32px",
            marginBottom: 28,
          }}
          data-testid="offer-section-problem-victory"
        >
          {/* Header row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 24px 1fr",
              gap: 14,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#b85a5a",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              3 PROBLÉMA
            </div>
            <div />
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#2a8a5a",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              3 GYŐZELEM
            </div>
          </div>
          {Array.from({ length: arcRows }).map((_, i) => {
            const num = String(i + 1).padStart(2, "0");
            const problem = top3Problema[i];
            const victory = top3Gyozelem[i];
            return (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 24px 1fr",
                  gap: 14,
                  alignItems: "stretch",
                  marginBottom: 10,
                }}
              >
                {/* Problem cell */}
                <div
                  style={{
                    background: "rgba(214,128,128,0.06)",
                    border: "1px solid rgba(214,128,128,0.18)",
                    borderRadius: 10,
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#b85a5a",
                      marginBottom: 4,
                    }}
                  >
                    {num}
                  </div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.45, color: "#1a1a28" }}>
                    {toText(problem)}
                  </div>
                </div>
                {/* Arrow */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#c0c0d0",
                    fontSize: 16,
                  }}
                >
                  →
                </div>
                {/* Victory cell */}
                <div
                  style={{
                    background: "rgba(76,181,133,0.07)",
                    border: "1px solid rgba(76,181,133,0.22)",
                    borderRadius: 10,
                    padding: "12px 14px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#2a8a5a",
                        marginBottom: 4,
                      }}
                    >
                      {num}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        lineHeight: 1.45,
                        color: "#0a0a12",
                        fontWeight: 500,
                      }}
                    >
                      {toText(victory)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SECTION 4 — A PROGRAM ÍVE (horizontal timeline; wraps to 2 rows for 7+ modules) */}
      {modulok.length > 1 && (
        <div
          style={{
            background: "white",
            border: "1px solid rgba(124,108,242,0.15)",
            borderRadius: 12,
            padding: "28px 32px",
            marginBottom: 28,
          }}
          data-testid="offer-section-program-arc"
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#7c6cf2",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: 22,
            }}
          >
            A PROGRAM ÍVE
          </div>
          {(() => {
            // Split into 2 rows only if there are 7+ modules.
            const needsSplit = modulok.length >= 7;
            const rows = needsSplit
              ? [
                  modulok.slice(0, Math.ceil(modulok.length / 2)),
                  modulok.slice(Math.ceil(modulok.length / 2)),
                ]
              : [modulok];

            const renderRow = (rowModules, rowIdx, offset) => (
              <div
                key={rowIdx}
                style={{
                  position: "relative",
                  padding: "8px 4px 24px",
                  marginTop: rowIdx > 0 ? 8 : 0,
                }}
              >
                {/* Connecting line */}
                <div
                  style={{
                    position: "absolute",
                    top: 28,
                    left: 24,
                    right: 24,
                    height: 2,
                    background:
                      "linear-gradient(to right, rgba(124,108,242,0.15), rgba(124,108,242,0.45), rgba(124,108,242,0.15))",
                  }}
                />
                {/* Nodes grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${rowModules.length}, 1fr)`,
                    gap: 8,
                    position: "relative",
                  }}
                >
                  {rowModules.map((m, i) => {
                    const globalIdx = offset + i;
                    const isLast = globalIdx === modulok.length - 1;
                    return (
                      <div key={i} style={{ textAlign: "center" }}>
                        <div
                          style={{
                            margin: "0 auto 12px",
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            border: "2px solid #7c6cf2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 600,
                            background: isLast ? "#7c6cf2" : "white",
                            color: isLast ? "white" : "#7c6cf2",
                          }}
                        >
                          {globalIdx + 1}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#2a2438",
                            lineHeight: 1.3,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                          }}
                        >
                          {arcLabel(m)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );

            let offset = 0;
            const rowEls = rows.map((row, idx) => {
              const el = renderRow(row, idx, offset);
              offset += row.length;
              return el;
            });

            return (
              <>
                {rowEls}
                {/* Week markers — only under the last row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    color: "#6a6a80",
                    marginTop: 4,
                    padding: "0 12px",
                  }}
                >
                  <span>1. hét</span>
                  <span>{endLabel}</span>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* SECTION 5 — MODULE CARDS (new layout: left circle + continuous body paragraph) */}
      {modulok.length > 0 && (
        <div style={{ marginBottom: 28 }} data-testid="offer-section-modules">
          <OEyebrow>A MODULOK RÉSZLETESEN</OEyebrow>
          {modulok.map((m, i) => {
            const highlight = toText(F(m, "highlight")) || "";
            const benefit = toText(F(m, "benefit")) || "";
            // Join into one paragraph with " - ". If either is empty, just use the
            // non-empty one. If both end with punctuation, normalize.
            const parts = [];
            if (highlight) parts.push(highlight.replace(/[.!?…]\s*$/, ""));
            if (benefit) parts.push(benefit.replace(/[.!?…]\s*$/, ""));
            const body = parts.length > 0 ? `${parts.join(" - ")}.` : "";
            // Új forward-only mezők: ha van, megjelennek 3 alpontban a body alatt.
            const mitTanul = toText(F(m, "mit_tanul"));
            const mitCsinal = toText(F(m, "mit_csinal"));
            const mitTapasztal = toText(F(m, "mit_tapasztal_a_het_vegere"));
            const hasNewFields = mitTanul || mitCsinal || mitTapasztal;
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 16,
                  alignItems: "flex-start",
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: "white",
                    border: "2px solid #7c6cf2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#7c6cf2",
                  }}
                >
                  M{i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: "#0a0a12",
                      marginBottom: 6,
                    }}
                  >
                    {toText(F(m, "nev"))}
                  </div>
                  {body && (
                    <div
                      style={{
                        fontSize: 14,
                        color: "#1a1a28",
                        lineHeight: 1.65,
                        marginBottom: hasNewFields ? 10 : 0,
                      }}
                    >
                      {body}
                    </div>
                  )}
                  {hasNewFields && (
                    <ul style={{
                      margin: 0, paddingLeft: 18,
                      fontSize: 13, color: "#1a1a28", lineHeight: 1.6,
                    }}>
                      {mitTanul && (
                        <li style={{ marginBottom: 4 }}>
                          <span style={{ color: "#7c6cf2", fontWeight: 600 }}>Mit tanul: </span>
                          {mitTanul}
                        </li>
                      )}
                      {mitCsinal && (
                        <li style={{ marginBottom: 4 }}>
                          <span style={{ color: "#7c6cf2", fontWeight: 600 }}>Mit csinál konkrétan: </span>
                          {mitCsinal}
                        </li>
                      )}
                      {mitTapasztal && (
                        <li style={{ marginBottom: 4 }}>
                          <span style={{ color: "#7c6cf2", fontWeight: 600 }}>Mit tapasztal a hét végére: </span>
                          {mitTapasztal}
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SECTION 6 — AZ AJÁNLAT (vertical white card: price / amit megkapsz / bonuses / guarantee / urgency) */}
      <div style={{ marginBottom: 28 }} data-testid="offer-section-grand-slam">
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#7c6cf2",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          AZ AJÁNLAT
        </div>
        <div
          style={{
            background: "white",
            border: "1px solid rgba(124,108,242,0.20)",
            borderRadius: 12,
            padding: "24px 28px",
          }}
        >
          {/* 6a — Price */}
          {F(pricing, "fo_ar") && (
            <div
              data-testid="offer-card-price"
              style={{
                textAlign: "center",
                paddingBottom: 20,
                borderBottom: "1px solid rgba(124,108,242,0.12)",
                marginBottom: 22,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#5e4cd8",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                PROGRAM ÁRA
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#0a0a12", lineHeight: 1 }}>
                {toText(F(pricing, "fo_ar"))}
              </div>
              {F(pricing, "reszletfizetes") && (
                <div style={{ fontSize: 13, color: "#6a6a80", marginTop: 6 }}>
                  vagy {toText(F(pricing, "reszletfizetes"))}
                </div>
              )}
            </div>
          )}

          {/* 6b — Amit megkapsz */}
          {amitMegkapsz.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#5e4cd8",
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                AMIT MEGKAPSZ
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {amitMegkapsz.map((item, i) => (
                  <li
                    key={i}
                    style={{
                      fontSize: 14,
                      color: "#1a1a28",
                      lineHeight: 1.55,
                      paddingLeft: 18,
                      position: "relative",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        left: 0,
                        color: "#7c6cf2",
                        fontWeight: 600,
                      }}
                    >
                      ✓
                    </span>
                    {toText(item)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 6c — Bonuses */}
          {bonuszok.length > 0 && (
            <div style={{ marginBottom: 22 }} data-testid="offer-card-bonuses">
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#5e4cd8",
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  marginBottom: 14,
                }}
              >
                {bonuszok.length} BÓNUSZ A PROGRAM MELLÉ
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {bonuszok.map((b, i) => (
                  <div
                    key={i}
                    style={{
                      background: "rgba(124,108,242,0.04)",
                      borderLeft: "3px solid #7c6cf2",
                      borderRadius: 6,
                      padding: "12px 14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "baseline",
                        marginBottom: 6,
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#7c6cf2" }}>
                        +{i + 1}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#0a0a12" }}>
                        {toText(F(b, "nev"))}
                      </span>
                    </div>
                    {F(b, "leiras") && (
                      <div style={{ fontSize: 13, color: "#2a2438", lineHeight: 1.55 }}>
                        {toText(F(b, "leiras"))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 6d — Guarantee */}
          {(F(garancia, "tipus") || F(garancia, "leiras")) && (
            <div
              data-testid="offer-card-guarantee"
              style={{
                background: "rgba(76,181,133,0.07)",
                border: "1px solid rgba(76,181,133,0.22)",
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 14,
              }}
            >
              {F(garancia, "tipus") && (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#2a8a5a",
                    letterSpacing: "0.10em",
                    textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  {toText(F(garancia, "tipus"))}
                </div>
              )}
              {F(garancia, "leiras") && (
                <div style={{ fontSize: 13.5, color: "#1a1a28", lineHeight: 1.55 }}>
                  {toText(F(garancia, "leiras"))}
                </div>
              )}
            </div>
          )}

          {/* 6e — Urgency */}
          {F(pricing, "surgosseg_szukosseg") && (
            <div
              style={{
                fontSize: 12,
                color: "#6a6a80",
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              {toText(F(pricing, "surgosseg_szukosseg"))}
            </div>
          )}
        </div>
      </div>

      {/* SECTION 7 — GYAKRAN ISMÉTELT KÉRDÉSEK (Q&A pairs, left purple border) */}
      {gyik.length > 0 && (
        <div
          style={{
            background: "white",
            border: "1px solid rgba(124,108,242,0.15)",
            borderRadius: 12,
            padding: "28px 32px",
            marginBottom: 28,
          }}
          data-testid="offer-section-gyik"
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#7c6cf2",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: 16,
            }}
          >
            GYAKRAN ISMÉTELT KÉRDÉSEK
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {gyik.map((qa, i) => (
              <div
                key={i}
                style={{
                  borderLeft: "3px solid rgba(124,108,242,0.30)",
                  padding: "0 0 4px 16px",
                }}
              >
                <div
                  style={{
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: "#0a0a12",
                    lineHeight: 1.45,
                    marginBottom: 8,
                  }}
                >
                  {toText(F(qa, "kerdes"))}
                </div>
                <div
                  style={{
                    fontSize: 13.5,
                    color: "#2a2438",
                    lineHeight: 1.65,
                  }}
                >
                  {toText(F(qa, "valasz"))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECTION 6 — ÜZENET-HORGOK (numbered left, hook + inline type label right) */}
      {hooks.length > 0 && (
        <div style={{ marginBottom: 28 }} data-testid="offer-section-hooks">
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#7c6cf2",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: 20,
            }}
          >
            ÜZENET-HORGOK
          </div>
          <div
            style={{
              background: "white",
              border: "1px solid rgba(124,108,242,0.15)",
              borderRadius: 12,
              padding: "32px 36px",
              marginBottom: 28,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {hooks.slice(0, 5).map((h, i) => {
                const tipus = toText(F(h, "tipus")).toLowerCase();
                const hookText = toText(F(h, "hook"));
                return (
                  <div
                    key={i}
                    style={{ display: "flex", gap: 14, alignItems: "flex-start" }}
                    data-testid={`offer-hook-${i}`}
                  >
                    <div
                      style={{
                        flexShrink: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#7c6cf2",
                        minWidth: 22,
                        paddingTop: 2,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div
                      style={{
                        fontSize: 14.5,
                        color: "#0a0a12",
                        lineHeight: 1.5,
                      }}
                    >
                      {hookText}
                      {tipus && (
                        <span style={{ fontSize: 12, color: "#6a6a80" }}>
                          {" "}• {tipus}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* SECTION 9 — ÖT POZÍCIONÁLÁSI MONDAT (forward-only) */}
      {Array.isArray(F(bp, "ot_pozicionalasi_mondat")) && F(bp, "ot_pozicionalasi_mondat").length > 0 && (
        <div style={{ marginBottom: 28 }} data-testid="offer-section-pozicionalas">
          <OEyebrow>ÖT POZÍCIONÁLÁSI MONDAT</OEyebrow>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {F(bp, "ot_pozicionalasi_mondat").map((item, i) => {
              const hangsulyMap = {
                FAJDALOM_FOKUSZU: "Fájdalom-fókuszú",
                EREDMENY_FOKUSZU: "Eredmény-fókuszú",
                IDENTITAS_FOKUSZU: "Identitás-fókuszú",
                IDOKERET_FOKUSZU: "Időkeret-fókuszú",
                KOZOSSEG_FOKUSZU: "Közösség-fókuszú",
              };
              const hangsuly = item?.hangsuly ? (hangsulyMap[item.hangsuly] || item.hangsuly) : "";
              const mondat = toText(item?.mondat);
              if (!mondat) return null;
              return (
                <div
                  key={i}
                  style={{
                    background: "white",
                    border: "1px solid rgba(124,108,242,0.15)",
                    borderRadius: 10,
                    padding: "14px 18px",
                  }}
                  data-testid={`offer-pozicionalas-${i + 1}`}
                >
                  {hangsuly && (
                    <div style={{
                      fontSize: 11, fontWeight: 600, color: "#7c6cf2",
                      letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6,
                    }}>
                      {i + 1}. {hangsuly}
                    </div>
                  )}
                  <div style={{ fontSize: 14, color: "#0a0a12", lineHeight: 1.55 }}>
                    {mondat}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SECTION 10 — TÁMOGATÁS ÉS ELSZÁMOLTATHATÓSÁG (forward-only) */}
      {F(bp, "tamogatas_es_elszamoltathatosag") && (
        Array.isArray(F(F(bp, "tamogatas_es_elszamoltathatosag"), "tamogatas")) ||
        Array.isArray(F(F(bp, "tamogatas_es_elszamoltathatosag"), "elszamoltathatosag"))
      ) && (
        <div style={{ marginBottom: 28 }} data-testid="offer-section-tamogatas">
          <OEyebrow>TÁMOGATÁS ÉS ELSZÁMOLTATHATÓSÁG</OEyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {Array.isArray(F(F(bp, "tamogatas_es_elszamoltathatosag"), "tamogatas")) &&
             F(F(bp, "tamogatas_es_elszamoltathatosag"), "tamogatas").length > 0 && (
              <div style={{
                background: "white",
                border: "1px solid rgba(76,181,133,0.22)",
                borderRadius: 10,
                padding: "16px 18px",
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: "#2a8a5a",
                  letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10,
                }}>
                  Támogatás
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: "#1a1a28", lineHeight: 1.65 }}>
                  {F(F(bp, "tamogatas_es_elszamoltathatosag"), "tamogatas").map((s, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>{toText(s)}</li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(F(F(bp, "tamogatas_es_elszamoltathatosag"), "elszamoltathatosag")) &&
             F(F(bp, "tamogatas_es_elszamoltathatosag"), "elszamoltathatosag").length > 0 && (
              <div style={{
                background: "white",
                border: "1px solid rgba(214,128,128,0.22)",
                borderRadius: 10,
                padding: "16px 18px",
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: "#b85a5a",
                  letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10,
                }}>
                  Elszámoltathatóság
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: "#1a1a28", lineHeight: 1.65 }}>
                  {F(F(bp, "tamogatas_es_elszamoltathatosag"), "elszamoltathatosag").map((s, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>{toText(s)}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OEyebrow({ children }) {
  return (
    <div
      style={{
        fontSize: 14,
        color: "#7c6cf2",
        letterSpacing: "0.12em",
        fontWeight: 600,
        marginBottom: 14,
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

function SnapLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: "#5e4cd8",
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        paddingTop: 3,
      }}
    >
      {children}
    </div>
  );
}

export function renderOfferBlueprint(bp) {
  if (!bp) return null;
  // V3 detection: new 19-section Ajánlat blueprint with E/2 inside-out narrative.
  if (bp.version === "v3") return renderOfferBlueprintV3(bp);
  // Legacy "old V2" detection (snapshot-based, kept for backwards compat).
  const isOldV2 =
    Object.prototype.hasOwnProperty.call(bp, "snapshot") ||
    Object.prototype.hasOwnProperty.call(bp, "messaging_hooks") ||
    Object.prototype.hasOwnProperty.call(bp, "egy_mondatos_ajanlat") ||
    Object.prototype.hasOwnProperty.call(bp, "gyik");
  if (isOldV2) return renderOfferBlueprintLegacy(bp);

  // Legacy renderer for older saved offers.
  const programName = F(bp, "ajanlat_nev", "program_name");
  const tagline = F(bp, "szlogen", "tagline");
  const coreProblem = F(bp, "alap_problema", "core_problem");
  const corePromise = F(bp, "alap_igeret", "core_promise");
  const whoFor = F(bp, "kinek_szol", "who_its_for");
  const whoNotFor = F(bp, "kinek_nem_szol", "who_its_not_for");
  const pillars = F(bp, "pillerek", "pillars");
  const deliverables = F(bp, "szolgaltatasok", "deliverables");
  const bonuses = F(bp, "bonuszok", "bonuses");
  const pricePositioning = F(bp, "arazas", "price_positioning");
  const guarantee = F(bp, "garancia", "guarantee");

  return (
    <div className="bp-stack space-y-6 bp-body leading-relaxed">
      <div className="p-5 rounded-2xl border border-[#5e50f7]/30 bg-gradient-to-br from-[#5e50f7]/[0.08] to-transparent">
        <Eyebrow>AJÁNLAT NEVE</Eyebrow>
        <div className="font-heading text-2xl mb-2">{toText(programName)}</div>
        {tagline && <div className="italic">"{toText(tagline)}"</div>}
      </div>
      {coreProblem && <Block label="ALAPPROBLÉMA" body={coreProblem} />}
      {corePromise && <Block label="ALAPÍGÉRET" body={corePromise} />}
      {Array.isArray(whoFor) && whoFor.length > 0 && (
        <List label="KINEK SZÓL" items={whoFor} />
      )}
      {Array.isArray(whoNotFor) && whoNotFor.length > 0 && (
        <List label="KINEK NEM SZÓL" items={whoNotFor} muted />
      )}
      {Array.isArray(pillars) && pillars.length > 0 && (
        <div>
          <Eyebrow>PILLÉREK</Eyebrow>
          <div className="space-y-2">
            {pillars.map((p, i) => (
              <div
                key={i}
                className="p-3 rounded-lg bg-[#1a1a1a] border border-white/[0.06]"
              >
                <div className="font-medium mb-1">
                  {i + 1}. {toText(F(p, "nev", "name"))}
                </div>
                <div className="text-[#a1a1a1] text-[12px]">
                  {toText(F(p, "leiras", "description"))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(deliverables) && deliverables.length > 0 && (
        <List label="SZOLGÁLTATÁSOK" items={deliverables} />
      )}
      {Array.isArray(bonuses) && bonuses.length > 0 && (
        <List label="BÓNUSZOK" items={bonuses} />
      )}
      {pricePositioning && <Block label="ÁRAZÁS" body={pricePositioning} />}
      {guarantee && (
        <div className="p-4 rounded-xl bg-[#1a1a1a] border-l-2 border-[#5e50f7]">
          <Eyebrow>GARANCIA</Eyebrow>
          <div className="italic">{toText(guarantee)}</div>
        </div>
      )}
    </div>
  );
}

/* ----------------------- Shared sub-components ----------------------- */

function Fact({ label, value }) {
  const text = toText(value);
  if (!text) return null;
  return (
    <div className="p-3 rounded-lg bg-[#1a1a1a] border border-white/[0.06]">
      <div
        className="text-[10px] uppercase tracking-[0.2em] text-[#a1a1a1] mb-1"
      >
        {label}
      </div>
      <div className="text-[13px]">{text}</div>
    </div>
  );
}

function SubList({ label, items }) {
  const arr = Array.isArray(items) ? items : items ? [items] : [];
  if (!arr.length) return null;
  return (
    <div className="p-3 rounded-lg bg-[#1a1a1a] border border-white/[0.06]">
      <div
        className="text-[10px] uppercase tracking-[0.2em] text-[#a1a1a1] mb-2"
      >
        {label}
      </div>
      <ul className="space-y-0.5">
        {arr.map((i, k) => (
          <li key={k} className="text-[13px]">
            • {toText(i)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <div
      className="mex-eyebrow"
      style={{
        fontSize: 11,
        color: "#7c6cf2",
        letterSpacing: "0.16em",
        fontWeight: 600,
        marginBottom: 7,
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

function Block({ label, body }) {
  if (body === null || body === undefined || body === "") return null;
  // Pass React nodes through unchanged; coerce raw data safely.
  const rendered = typeof body === "string" || React.isValidElement(body)
    ? body
    : toText(body);
  if (!rendered) return null;
  return (
    <div style={{ marginBottom: 22 }}>
      <Eyebrow>{label}</Eyebrow>
      <div style={{ fontSize: 14, color: "#1a1a28", lineHeight: 1.65 }}>{rendered}</div>
    </div>
  );
}

function List({ label, items, muted }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ marginBottom: 22 }}>
      <Eyebrow>{label}</Eyebrow>
      <ul className="space-y-1.5">
        {items.map((item, k) => (
          <li
            key={k}
            style={{
              fontSize: 14,
              color: muted ? "#6a6a78" : "#1a1a28",
              textDecoration: muted ? "line-through" : "none",
              lineHeight: 1.7,
              paddingLeft: 0,
            }}
          >
            <span style={{ color: "#7c6cf2", fontWeight: 600, marginRight: 8 }}>·</span>
            {toText(item)}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Webinár renderer — a Tudástárba mentett webinár blueprint-je egy
// `carousel_content` tömböt tartalmaz: dia-szintű objektumokat (pill,
// visible_headline, visible_bullets, visible_question, visible_cta,
// visual_suggestion, speaker_notes, section_id, section_name). A
// renderer szekciónként csoportosítva jeleníti meg a slide-okat egy
// egyszerű, olvasható előnézetben. Design-tolerálva: a Carousel-lel
// megegyező vizuális hangzású, de az előnézetben egyszerűbb layout.
export function renderWebinarBlueprint(blueprint) {
  if (!blueprint || typeof blueprint !== "object") {
    return (
      <div className="p-6 text-sm text-[#6a6a78]">Nincs betöltött webinár tartalom.</div>
    );
  }
  // Backend save shape kompatibilitás: a webinár blueprint
  // `carousel_content`-je LEHET (a) sima Array (a v7 Section Carousel
  // frontend így küldi el save-kor), VAGY (b) egy `{diak:[…], leiras,
  // miert_mukodik, alternativ_horgok, webinar_cim}` object (a régi
  // callbook-funnel writer parse-olt formája). A renderernek mindkettőt
  // le kell kezelnie — a slide-lista MINDIG `dia_lista`-ba kerül.
  const cc = blueprint.carousel_content;
  const dia_lista = Array.isArray(cc)
    ? cc
    : Array.isArray(cc?.diak)
      ? cc.diak
      : [];
  const fallbackText = typeof blueprint.full_text === "string" ? blueprint.full_text : "";

  // Fallback: ha nincs carousel_content, de van full_text, azt
  // mutassuk pre-wrap formában.
  if (!dia_lista.length) {
    if (fallbackText) {
      return (
        <div className="p-6 text-sm text-[#3a4654]" style={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }}>
          {fallbackText}
        </div>
      );
    }
    return (
      <div className="p-6 text-sm text-[#6a6a78]">Nincs betöltött webinár tartalom.</div>
    );
  }

  // Szekciónkénti csoportosítás — section_id / section_name alapján.
  const bySection = [];
  let curr = null;
  for (const slide of dia_lista) {
    const sid = slide?.section_id || "_none";
    if (!curr || curr.sid !== sid) {
      curr = { sid, name: slide?.section_name || "", slides: [] };
      bySection.push(curr);
    }
    curr.slides.push(slide);
  }

  return (
    <div className="px-8 py-6" style={{ background: "#fbf7ee" }}>
      {bySection.map((sec, idx) => (
        <div key={sec.sid + idx} className="mb-8">
          {sec.name && (
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8f6a1a] mb-3">
              {sec.name}
            </div>
          )}
          <div className="flex flex-col gap-3">
            {sec.slides.map((slide, i) => (
              <div
                key={i}
                className="rounded-lg p-4"
                style={{ background: "#ffffff", border: "0.5px solid rgba(20,30,40,0.08)" }}
              >
                {slide.pill && (
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8f6a1a] mb-1">
                    Slide {slide.dia_szam} · {slide.pill}
                  </div>
                )}
                {slide.visible_headline && (
                  <div className="font-heading text-[16px] font-bold text-[#0a0a12] leading-snug mb-2">
                    {slide.visible_headline}
                  </div>
                )}
                {Array.isArray(slide.visible_bullets) && slide.visible_bullets.length > 0 && (
                  <ul className="list-none p-0 m-0 flex flex-col gap-1.5">
                    {slide.visible_bullets.map((b, bi) => (
                      <li key={bi} className="text-[13px] text-[#3a4654] leading-relaxed pl-4 relative">
                        <span
                          className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full"
                          style={{ background: "#c9a24a" }}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                {slide.visible_question && (
                  <div className="mt-2 text-[13px] italic text-[#8f6a1a]">
                    {slide.visible_question}
                  </div>
                )}
                {slide.visible_cta && (
                  <div className="mt-3 inline-flex items-center px-3 py-1.5 rounded-full text-[12px] font-semibold text-white" style={{ background: "#c9a24a" }}>
                    {slide.visible_cta}
                  </div>
                )}
                {slide.speaker_notes && (
                  <div className="mt-3 text-[12px] text-[#6a6a78] leading-relaxed border-t border-[rgba(20,30,40,0.06)] pt-2" style={{ whiteSpace: "pre-wrap" }}>
                    <span className="font-semibold">Előadói jegyzet:</span> {slide.speaker_notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


export const RENDERERS = {
  voice: renderVoiceBlueprint,
  client: renderAvatarBlueprint,
  offer: renderOfferBlueprint,
  "webinar": renderWebinarBlueprint,
  "webinar-callbook-funnel": renderWebinarBlueprint,
  "webinar-callback-funnel": renderWebinarBlueprint,
  "webinar-direct-sales": renderWebinarBlueprint,
  "webinar-vazlat": renderWebinarBlueprint,
  // Landing regisztrációs oldal ugyanazt a Webinár blueprint renderert
  // használja, mert a carousel_content shape identikus (slides tömb).
  "landing-webinar-registration": renderWebinarBlueprint,
};

export const AGENT_LABELS = {
  voice: { name: "Márkahang (Brand Voice)", endpoint: "/foundation/brand-voice", kind: "voice", route: "/agents/voice" },
  client: { name: "Ideális Ügyfélprofil (Avatar)", endpoint: "/foundation/avatar", kind: "client", route: "/agents/client" },
  offer: { name: "Prémium Ajánlat (Offer)", endpoint: "/foundation/offer", kind: "offer", route: "/agents/offer" },
  // Webinárok: a Tudástár item eleve inline blueprint-tel jön, így
  // endpoint = null (a modal az `item.blueprint`-et használja). Minden
  // webinár sub-variant a KÖZÖS `/agents/webinar` varázslóra navigál —
  // App.js-ben nincs külön route az agent_type slug-oknak.
  "webinar":                 { name: "Webinár",                     endpoint: null, kind: "webinar", route: "/agents/webinar" },
  "webinar-callbook-funnel": { name: "Webinár – telefonhívás",      endpoint: null, kind: "webinar", route: "/agents/webinar" },
  "webinar-callback-funnel": { name: "Webinár – telefonhívás",      endpoint: null, kind: "webinar", route: "/agents/webinar" },
  "webinar-direct-sales":    { name: "Webinár – direkt értékesítés", endpoint: null, kind: "webinar", route: "/agents/webinar" },
  "webinar-vazlat":          { name: "Webinár vázlat",              endpoint: null, kind: "webinar", route: "/agents/webinar" },
  // Landing Varázsló · Webinár regisztrációs oldal — a slug backend-oldalon
  // `landing-webinar-registration`. A megjelenítő shape (carousel_content)
  // ugyanaz mint a webinárnál, ezért a preview modal ugyanazt a Webinár
  // rendererét használja. A user-facing label magyar és tiszta, nincs
  // technikai slug szivárgás.
  "landing-webinar-registration": { name: "Webinár regisztrációs oldal", endpoint: null, kind: "webinar", route: "/agents/landing" },
};
